/**
 * Zentrale Registratur aller übersetzbaren UI-Texte.
 *
 * - Keys sind sprechende, namespacierte Strings (z.B. "nav.suites").
 * - `de` ist die deutsche Originalfassung (Quelle der Wahrheit).
 * - `en` ist die englische Default-Übersetzung. Wird im Dashboard
 *   überschrieben (DB), die DB-Version hat Vorrang.
 * - `html: true` markiert Texte mit HTML-Tags (DeepL `tag_handling=html`).
 *
 * Regel: Niemals `locale === "en" ? "..." : "..."` im Code.
 * Stattdessen: t("nav.suites").
 */
export interface TranslationEntry {
  de: string;
  en?: string;
  html?: boolean;
}

export const translations = {
  // === Header / Navigation ===
  "nav.suites": { de: "Suites" },
  "nav.suites_overview": { de: "Übersicht" },
  "nav.suites_ausstattung": { de: "Ausstattung" },
  "nav.suites_buchung": { de: "Buchungsinformationen" },
  "nav.lerch_plus": { de: "Lerch Plus" },
  "nav.wellness": { de: "Wellness" },
  "nav.erlebnisse": { de: "Erlebnisse" },
  "nav.erlebnisse_paznaun": { de: "Erlebnisse im Paznaun" },
  "nav.sommer": { de: "Sommer" },
  "nav.winter": { de: "Winter" },
  "nav.events": { de: "Events" },

  "header.skip_to_content": { de: "Zum Inhalt springen" },
  "header.menu_open": { de: "Menü öffnen" },
  "header.menu_close": { de: "Menü schließen" },
  "header.aria_main_left": { de: "Hauptnavigation links" },
  "header.aria_main_right": { de: "Hauptnavigation rechts" },
  "header.aria_mobile": { de: "Mobile Navigation" },
  "header.aria_home": { de: "Startseite" },
  "header.aria_call": { de: "Anrufen" },
  "header.aria_mail": { de: "E-Mail schreiben" },
  "header.aria_language": { de: "Sprache wechseln" },

  // === Footer ===
  "footer.newsletter_title": { de: "Newsletter abonnieren" },
  "footer.newsletter_cta": { de: "Jetzt anmelden" },
  "footer.contact_title": { de: "Kontakt" },
  "footer.opening_title": { de: "Öffnungszeiten" },
  "footer.legal_imprint": { de: "Impressum" },
  "footer.legal_privacy": { de: "Datenschutz" },
  "footer.legal_accessibility": { de: "Barrierefreiheitserklärung" },
  "footer.legal_sitemap": { de: "Sitemap" },
  "footer.site_by": { de: "Site by Huber Web Media" },
  "footer.nav.lerch_plus": { de: "Lerch Plus" },
  "footer.nav.wellness": { de: "Wellness" },
  "footer.nav.erlebnisse": { de: "Erlebnisse" },
  "footer.nav.lage": { de: "Lage & Anreise" },
  "footer.nav.buchung": { de: "Buchungsinformationen" },
  "footer.nav.bewertungen": { de: "Alle Bewertungen" },

  // === Sticky CTAs / Common Buttons ===
  "cta.book": { de: "Jetzt buchen" },
  "cta.inquire": { de: "Anfragen" },
  "cta.more": { de: "Mehr erfahren" },
  "cta.read_more": { de: "Weiterlesen" },
  "cta.back": { de: "Zurück" },

  // === 404 ===
  "notfound.title": { de: "Seite nicht gefunden" },
  "notfound.text": {
    de: "Die von Ihnen gesuchte Seite existiert leider nicht oder wurde verschoben. Kehren Sie zur Startseite zurück, um Ihre Reise fortzusetzen.",
    
  },
  "notfound.cta": { de: "Zur Startseite" },

  // === Startseite – Hero ===
  "home.hero.aria_label": {
    de: "Willkommen in den Lerch Suites",
    
  },
  "home.hero.subline": {
    de: "Raum für\nechten Urlaub",
    
  },

  // === Startseite – Intro ===
  "home.intro.subline": {
    de: "Entspannt ankommen.\nGenussvoll wohnen.",
    
  },
  "home.intro.headline": {
    de: "Apartments in\u00A0Kappl",
    
  },
  "home.intro.p1": {
    de: "Wir – Ihre Gastgeberfamilie Rebecca und Ramiro Lerch – heißen Sie herzlich in den Lerch Suites willkommen. Unsere <strong>Apartments in Kappl</strong>, nur sieben Minuten von Ischgl, bieten Ihnen in ruhiger Lage den idealen Ausgangspunkt für Ihren Urlaub im Paznaun in Tirol.",
    
    html: true,
  },
  "home.intro.p2": {
    de: "Unsere großzügigen Apartments sind <strong>hochwertig eingerichtet</strong> und schenken Ihnen ausreichend Raum, um anzukommen und sich wohlzufühlen. Wir gestalten Ihre Zeit bei uns unkompliziert und richten uns voll und ganz nach Ihren Bedürfnissen.",
    
    html: true,
  },
  "home.intro.p3": {
    de: "Erleben Sie bei uns einen <strong>unvergesslichen und familienfreundlichen Urlaub</strong> in Kappl, der Komfort und Entspannung perfekt vereint.",
    
    html: true,
  },
  "home.intro.image_alt": {
    de: "Gastgeberfamilie Rebecca und Ramiro Lerch mit Kindern",
    
  },

  // === Startseite – Suites Grid ===
  "home.suites_grid.kicker": {
    de: "Großzügige\nApartments",
    
  },
  "home.suites_grid.headline": { de: "Unsere Suites" },
  "home.suites_grid.body": {
    de: "Ob für Familien, Paare oder Freunde – unsere Lerch Suites bieten Ihnen den perfekten Rückzugsort für Ihren Urlaub in Kappl. Modern eingerichtet, hochwertig ausgestattet und geräumig geschnitten versprechen unsere Apartments viel Platz für gemeinsame Zeit und gleichzeitig persönlichen Freiraum.",
    
  },
  "home.suites_grid.cta_all": { de: "Alle Apartments" },
  "suites.overview.headline": { de: "Großzügige\nApartments" },
  "home.suites_grid.persons_suffix": { de: "Personen" },
  "home.suites_grid.details": { de: "Details" },
  "home.suites_grid.inquire": { de: "Anfragen" },
  "home.suites_grid.book": { de: "buchen" },

  // === Startseite – Lerch Plus ===

  "home.lerch_plus.kicker": {
    de: "Ihre Vorteile bei uns",
    
  },
  "home.lerch_plus.headline": { de: "Lerch Plus" },
  "home.lerch_plus.body": {
    de: "Die Lerch Suites verbinden die ruhige Lage in Kappl mit der Nähe zu Ischgl und bieten Ihnen damit maximale Flexibilität für Ihren Urlaub. Unsere hochwertigen Apartments sorgen für Komfort und viel Raum zum Wohlfühlen.\n\nWählen Sie in unserer exklusiven Frühstücks-App wahlweise ein komplettes Frühstück oder nur frische Brötchen und bestellen Sie bis 21:00 Uhr am Vortag. Wir liefern Ihre Auswahl morgens um ca. 7:00 Uhr direkt zu Ihrem Apartment. Dazu erhalten Sie ausgewählte Restauranttipps in der Umgebung – für genussvolle und unkomplizierte Urlaubstage. All das zusammen und noch mehr – das ist unser Lerch Plus.",
    
  },
  "home.lerch_plus.cta_label": {
    de: "Alle Vorteile",
    
  },

  // === Startseite – Wellness ===
  "home.wellness.kicker": {
    de: "Ganzheitliche Entspannungsmomente",
    
  },
  "home.wellness.headline": { de: "Wellness" },
  "home.wellness.text": {
    de: "Nach einem aktiven Tag in den Bergen rund um Kappl und Ischgl können Sie in unserem kleinen Wellnessbereich entspannen und neue Energie tanken. Unser Spa ist im Winter täglich von 16:00 bis 20:00 Uhr geöffnet, im Sommer auf Anfrage verfügbar.",
    
  },
  "home.wellness.cta_label": {
    de: "Zum Wellnessbereich",
    
  },
  "home.wellness.image_alt": {
    de: "Wellnessbereich",
    
  },

  // === Startseite – Erlebnisse ===
  "home.erlebnisse.kicker": {
    de: "Sommer wie Winter vielfältig unterwegs",
    
  },
  "home.erlebnisse.headline": {
    de: "Erlebnisse im Paznauntal",
    
  },
  "home.erlebnisse.text": {
    de: "Das Paznaun bietet Sommer wie Winter zahlreiche Möglichkeiten für aktive und erholsame Urlaubstage. Erkunden Sie die Bergwelt bei Wanderungen, genießen Sie Familienzeit im Sunny Mountain Erlebnispark in Kappl oder erleben Sie Skivergnügen in Ischgl. Auch Veranstaltungen und besondere Events in der Region machen Ihren Aufenthalt abwechslungsreich und vielseitig.",
    
  },
  "home.erlebnisse.cta_label": { de: "Mehr erfahren" },
  "home.erlebnisse.tile_sommer": {
    de: "Sommererlebnisse",
    
  },
  "home.erlebnisse.tile_winter": {
    de: "Wintererlebnisse",
    
  },
  "home.erlebnisse.tile_top_events": {
    de: "Top\u2011Events",
    
  },

  // === Startseite – Instagram ===
  "home.instagram.kicker": {
    de: "Einblicke in Ihren Urlaub",
    
  },
  "home.instagram.headline": {
    de: "Lerch Suites auf Instagram",
    
  },
  "home.instagram.text": {
    de: "Folgen Sie uns auf Instagram und entdecken Sie die Lerch Suites, unsere Apartments, und die Region rund um Kappl und Ischgl.",
    
  },
  "home.instagram.aria_profile": {
    de: "Lerch Suites auf Instagram (öffnet in neuem Tab)",
    
  },
  "home.instagram.profile_url": { de: "https://www.instagram.com/lerch_suites/?locale=de" },
  "home.instagram.image_alt_fallback": {
    de: "Lerch Suites Instagram",
    
  },

  // === Startseite – Bewertungen ===
  "home.bewertungen.kicker": {
    de: "Das sagen unsere Gäste",
    
  },
  "home.bewertungen.headline": { de: "Bewertungen" },
  "home.bewertungen.text": {
    de: "Lesen Sie, was unsere Gäste über ihren Aufenthalt in den Lerch Suites und unsere Apartments in Kappl berichten. Authentische Bewertungen, die Vertrauen schaffen und die Qualität unseres Hauses unterstreichen.",
    
  },
  "home.bewertungen.cta_label": {
    de: "Alle Bewertungen",
    
  },
  "home.bewertungen.read_more": { de: "Weiterlesen" },
  "home.bewertungen.dot_aria": { de: "Bewertung" },
  "home.bewertungen.guest_fallback": { de: "Gast" },
  "home.bewertungen.dummy1_name": { de: "Andreas L" },
  "home.bewertungen.dummy1_text": {
    de: "Wunderbares Apartment, super Ausstattung und eine super nette Familie! Absolut empfehlenswert !!",
    
  },
  "home.bewertungen.dummy2_name": { de: "Familie Müller" },
  "home.bewertungen.dummy2_text": {
    de: "Wunderschöne Apartments, traumhafte Lage und herzliche Gastgeber. Wir kommen sicher wieder!",
    
  },
  "home.bewertungen.dummy3_name": { de: "Anna & Tom" },
  "home.bewertungen.dummy3_text": {
    de: "Stilvoll eingerichtet, sehr sauber, perfekter Ausgangspunkt für Skitouren rund um Ischgl.",
    
  },
  "home.bewertungen.dummy4_name": { de: "Markus K." },
  "home.bewertungen.dummy4_text": {
    de: "Tolle Suite, liebevolle Details und ein wirklich entspannender Wellnessbereich. Absolute Empfehlung.",
    
  },

  // === Wellness-Seite ===
  "wellness.seo.title": { de: "Ferienwohnung Kappl mit Sauna & Infrarotkabine | Wellness" },
  "wellness.seo.description": {
    de: "Sauna, Fußbad, Infrarotkabine und Ruhebereich im Aparthotel Lerch in Kappl – plus die Silvretta Therme Ischgl ganz in der Nähe.",
    
  },
  "wellness.hero.subline": { de: "Für erholsame Augenblicke" },
  "wellness.hero.headline": {
    de: "Wellness –<br />Ihre Auszeit im Lerch",
    
    html: true,
  },
  "wellness.hero.image_alt": { de: "Wellnessbereich im Aparthotel Lerch" },
  "wellness.intro": {
    de: "Nach einem erlebnisreichen Tag in den Bergen bietet unser <strong>kleiner Wellnessbereich</strong> einen angenehmen Ort zum Entspannen. Nehmen Sie sich Zeit, um zur Ruhe zu kommen und neue Energie zu tanken. Im Winter ist der Wellnessbereich <strong>täglich von 16:00 bis 20:00 Uhr</strong> geöffnet, im <strong>Sommer auf Anfrage</strong>. Bademäntel erhalten Sie gegen eine kleine Leihgebühr an der Rezeption. Kostenlose Saunatücher finden Sie im Wellnessbereich.",
    
    html: true,
  },
  "wellness.sauna.title": { de: "Sauna" },
  "wellness.sauna.subline": { de: "Wohltuende Wärme" },
  "wellness.sauna.text": {
    de: "Genießen Sie die <strong>wohltuende Wärme</strong> unserer Sauna und nehmen Sie sich Zeit zum Abschalten. Ein ruhiger Ort, um Körper und Geist zu entspannen.",
    
    html: true,
  },
  "wellness.fussbad.title": { de: "Fußbad" },
  "wellness.fussbad.subline": { de: "Entspannung für müde Füße" },
  "wellness.fussbad.text": {
    de: "Zwei Becken laden dazu ein, bequem Platz zu nehmen und ein <strong>wohltuendes Fußbad</strong> zu genießen. Ideal, um nach einem aufregenden Tag in den Bergen zur Ruhe zu kommen.",
    
    html: true,
  },
  "wellness.infrarot.title": { de: "Infrarotkabine" },
  "wellness.infrarot.subline": { de: "Wohltuende Tiefenwärme" },
  "wellness.infrarot.text": {
    de: "Unsere kleine Infrarotkabine sorgt für eine gleichmäßige, <strong>wohltuende Tiefenwärme</strong> und bietet einen ruhigen Rückzugsort nach einem aktiven Tag. Das <strong>frische Leitungswasser</strong> aus unserer Region tut zwischendurch besonders gut.",
    
    html: true,
  },
  "wellness.ruhebereich.title": { de: "Ruhebereich" },
  "wellness.ruhebereich.subline": { de: "Stille Momente" },
  "wellness.ruhebereich.text": {
    de: "Unser <strong>stilvoll gestalteter Ruhebereich</strong> bietet Ihnen einen angenehmen Ort, um nach dem Saunagang zur Ruhe zu kommen. Bequeme Liegen und eine ruhige Atmosphäre sorgen für entspannte Momente.",
    
    html: true,
  },
  "wellness.silvretta.title": { de: "Silvretta Therme Ischgl" },
  "wellness.silvretta.subline": { de: "Wellness erweitern" },
  "wellness.silvretta.text": {
    de: "Wenn Sie Ihr Wellnessangebot erweitern möchten, erreichen Sie die <strong>Silvretta Therme</strong> in Ischgl in kurzer Zeit. Diese bietet auf mehreren Ebenen moderne Wasser- und Saunawelten mit Innen- und Außenpools, verschiedenen Saunen sowie großzügigen Ruhebereichen. Ergänzt wird das Angebot durch ein Sportbecken und stilvolle Rückzugsorte.",
    
    html: true,
  },
  "wellness.silvretta.cta_label": { de: "Mehr zur Silvretta Therme" },
  "wellness.silvretta.cta_aria": {
    de: "Mehr zur Silvretta Therme (öffnet in neuem Tab)",
    
  },
  "wellness.silvretta.cta_url": { de: "https://www.silvrettatherme.at/de" },

  // === Bewertungen-Seite ===
  "bewertungen.seo.title": { de: "Was unsere Gäste sagen" },
  "bewertungen.headline": { de: "Was unsere Gäste sagen" },
  "bewertungen.subline": { de: "Alle Bewertungen auf einen Blick" },
  "bewertungen.intro": {
    de: "Über die Jahre durften wir viele wunderbare Gäste<br />in den Lerch Suites willkommen heißen. Lesen Sie hier <strong>authentische Stimmen</strong> von Familien, Paaren und Reisenden, die ihren Urlaub in Kappl bei uns verbracht haben – <strong>ehrliche Eindrücke</strong>, die zeigen, was unser Haus&nbsp;ausmacht.",
    
    html: true,
  },
  "bewertungen.rating_count_label": { de: "Bewertungen" },
  "bewertungen.guest_fallback": { de: "Gast" },
  "bewertungen.cta.title": { de: "Werden auch Sie unser Gast" },
  "bewertungen.cta.text": {
    de: "Erleben Sie selbst, was unsere Gäste an den Lerch Suites schätzen – und buchen Sie Ihren nächsten Urlaub in Kappl direkt bei uns.",
    
  },
  "bewertungen.dummy1_name": { de: "Andreas L." },
  "bewertungen.dummy1_date": { de: "März 2026" },
  "bewertungen.dummy1_title": { de: "Wunderbares Apartment" },
  "bewertungen.dummy1_text": {
    de: "Wunderbares Apartment, super Ausstattung und eine super nette Familie! Absolut empfehlenswert!",
    
  },
  "bewertungen.dummy2_name": { de: "Familie Müller" },
  "bewertungen.dummy2_date": { de: "Februar 2026" },
  "bewertungen.dummy2_title": { de: "Traumhafte Lage" },
  "bewertungen.dummy2_text": {
    de: "Wunderschöne Apartments, traumhafte Lage und herzliche Gastgeber. Wir kommen sicher wieder!",
    
  },
  "bewertungen.dummy3_name": { de: "Anna & Tom" },
  "bewertungen.dummy3_date": { de: "Januar 2026" },
  "bewertungen.dummy3_title": { de: "Perfekt für Skitouren" },
  "bewertungen.dummy3_text": {
    de: "Stilvoll eingerichtet, sehr sauber, perfekter Ausgangspunkt für Skitouren rund um Ischgl. Wir haben uns rundum wohlgefühlt.",
    
  },

  // === Buchen-Seite ===
  "buchen.seo.title": { de: "Jetzt Ihren Traumurlaub im Lerch buchen" },
  "buchen.seo.description": {
    de: "Buchen Sie Ihr Apartment in den Lerch Suites direkt über unsere Website und profitieren Sie von unserer Bestpreisgarantie.",
    
  },
  "buchen.kicker": { de: "Mit Bestpreisgarantie" },
  "buchen.headline": {
    de: "Jetzt Ihren Traumurlaub<br />im Lerch buchen",
    
    html: true,
  },
  "buchen.body": {
    de: "Buchen Sie Ihr Apartment in den Lerch Suites direkt über unsere Website und profitieren Sie von unserer Bestpreisgarantie. Einfach, sicher und zum besten verfügbaren Preis.",
    
  },
  "buchen.widget.loading": { de: "Buchungswidget wird geladen …" },
  "buchen.widget.error": { de: "Buchungswidget konnte nicht geladen werden." },

  // === Anfrage-Seite ===
  "anfrage.seo.title": { de: "Unverbindliche Anfrage für Ihren Aufenthalt im Lerch" },
  "anfrage.seo.description": {
    de: "Senden Sie uns Ihre unverbindliche Anfrage – wir melden uns zeitnah mit einem individuellen Angebot, abgestimmt auf Ihre Wünsche.",
    
  },
  "anfrage.kicker": { de: "Unverbindliche Anfrage" },
  "anfrage.headline": { de: "FÜR IHREN AUFENTHALT IM LERCH" },
  "anfrage.body": {
    de: "Senden Sie uns gerne Ihre Anfrage – wir melden uns zeitnah bei Ihnen mit einem individuellen Angebot, abgestimmt auf Ihre Wünsche.",
    
  },
  "anfrage.widget.loading": { de: "Anfrageformular wird geladen …" },
  "anfrage.widget.error": { de: "Anfrageformular konnte nicht geladen werden." },

  // === Sticky CTA Bar ===
  "sticky.aria_region": { de: "Schnellbuchung" },
  "sticky.label_arrival": { de: "Anreise" },
  "sticky.label_departure": { de: "Abreise" },
  "sticky.pick_date": { de: "Datum wählen" },
  "sticky.btn_book": { de: "Buchen" },
  "sticky.btn_inquire": { de: "Anfragen" },

  // === Smallsearch (Easybooking) ===
  "smallsearch.label_arrival": { de: "Anreise" },
  "smallsearch.label_departure": { de: "Abreise" },
  "smallsearch.placeholder_date": { de: "Datum wählen" },
  "smallsearch.btn_book": { de: "Buchen" },
  "smallsearch.btn_inquire": { de: "Anfragen" },

  // === WhatsApp Button ===
  "whatsapp.aria": {
    de: "WhatsApp Chat öffnen",
    
  },
  "whatsapp.title": { de: "Lerch Suites WhatsApp" },
  "whatsapp.greeting": {
    de: "Hallo und herzlich willkommen! Wie können wir Ihnen helfen?",
    
  },
  "whatsapp.placeholder": { de: "Ihre Nachricht…" },
  "whatsapp.send": { de: "Senden" },
  "whatsapp.close": { de: "Schließen" },
  "whatsapp.hint": {
    de: "Beim Senden öffnet sich WhatsApp in einem neuen Tab.",
    
  },

  // === Newsletter Dialog ===
  "newsletter.kicker": { de: "Auf dem Laufenden bleiben" },
  "newsletter.title": { de: "Newsletter abonnieren" },
  "newsletter.label_firstname": { de: "Vorname" },
  "newsletter.label_lastname": { de: "Nachname" },
  "newsletter.label_email": { de: "E-Mail" },
  "newsletter.privacy_text_before": { de: "Ich akzeptiere die " },
  "newsletter.privacy_link": { de: "Datenschutzerklärung" },
  "newsletter.privacy_link_aria": {
    de: "Datenschutzerklärung (öffnet in neuem Tab)",
    
  },
  "newsletter.privacy_text_after": { de: "." },
  "newsletter.privacy_value": { de: "Ich akzeptiere die Datenschutzerklärung." },
  "newsletter.submit": { de: "Anmelden" },
  "newsletter.note": { de: "Sie können den Newsletter jederzeit abbestellen." },

  // === Buchungsinformationen (Section auf Suites-Detail) ===
  "booking.kicker": { de: "Alles Wichtige für Ihren Aufenthalt" },
  "booking.headline": { de: "Buchungs\u00ADinformationen" },

  "booking.anreise.title": { de: "An- und Abreise" },
  "booking.anreise.content": {
    de: "<p><strong>Check-In</strong> ist ab 15:00 Uhr möglich. Sollte Ihr Apartment bereits früher bezugsfertig sein, können Sie dieses gerne früher nutzen. Bei späterer Anreise bitten wir um kurze Information – Ihre Schlüsselkarte wird im Key-Safe hinterlegt.</p><p><strong>Check-Out</strong> erfolgt bis 09:30 Uhr. Ihre Schlussrechnung begleichen Sie bitte am Abend vor der Abreise <strong>bis 19:00 Uhr an der Rezeption</strong>.</p><p>Für eine noch einfachere Ankunft steht Ihnen unser <strong>Online Check-In</strong> zur Verfügung.</p>",
    
    html: true,
  },

  "booking.bezahlung.title": { de: "Bezahlung & Anzahlung" },
  "booking.bezahlung.content": {
    de: "<p>Ihre Buchung ist mit Eingang einer <strong>Anzahlung von 30 % des Gesamtpreises</strong> verbindlich.</p><p>Die <strong>Restzahlung</strong> ist bitte ein paar Tage vor Anreise per Überweisung zu begleichen. Die Ortstaxe ist in diesem Betrag bereits enthalten.</p><p>Wir akzeptieren Barzahlung, EC-Karte, Kreditkarte (Visa, MasterCard) sowie Überweisung im Voraus.</p>",
    
    html: true,
  },

  "booking.storno.title": { de: "Stornobedingungen" },
  "booking.storno.content": {
    de: `<p>Es gelten die Stornobedingungen laut AGBH:</p><ul><li>bis 3 Monate vor Anreise: kostenfrei</li><li>3 Monate bis 1 Monat: 40 %</li><li>1 Monat bis 1 Woche: 70 %</li><li>letzte Woche: 90 %</li><li>bei Nichtanreise: 100 %</li></ul><p>Wir empfehlen den Abschluss einer Reisestornoversicherung, z. B. bei der <a href="https://www.europaeische.at" target="_blank" rel="noopener noreferrer" class="underline underline-offset-2 hover:text-[#6C184F] transition-colors">Europäischen Reiseversicherung</a>.</p>`,
    en: `<p>The cancellation terms of the Austrian Hotel Contract Conditions (AGBH) apply:</p><ul><li>up to 3 months before arrival: free of charge</li><li>3 months to 1 month: 40 %</li><li>1 month to 1 week: 70 %</li><li>last week: 90 %</li><li>no-show: 100 %</li></ul><p>We recommend taking out travel cancellation insurance, e.g. with <a href="https://www.europaeische.at/en" target="_blank" rel="noopener noreferrer" class="underline underline-offset-2 hover:text-[#6C184F] transition-colors">Europäische Reiseversicherung</a>.</p>`,
    html: true,
  },

  "booking.endreinigung.title": { de: "Endreinigung" },
  "booking.endreinigung.content": {
    de: "<p>Für die Endreinigung berechnen wir zusätzlich eine einmalige Gebühr.</p>",
    
    html: true,
  },

  "booking.ortstaxe.title": { de: "Ortstaxe" },
  "booking.ortstaxe.content": {
    de: "<p>Die Ortstaxe beträgt € 5 pro Person und Tag (ab 15 Jahren).</p>",
    
    html: true,
  },

  "booking.kurzlang.title": { de: "Kurz- & Langaufenthalt" },
  "booking.kurzlang.content": {
    de: "<p>Bei Aufenthalten bis zu drei Nächten wird ein Zuschlag berechnet.</p><p>Ab einem Aufenthalt von sieben Nächten profitieren Sie von einem Nachlass. Ausgenommen sind bestimmte Saisonzeiten – gerne informieren wir Sie persönlich.</p>",
    
    html: true,
  },

  "booking.gaestemappe.title": { de: "Digitale Gästemappe" },
  "booking.gaestemappe.content": {
    de: "<p>Mit unserer Gästemappe-App erhalten Sie Informationen zu Anreise, Angeboten, Events und Services während Ihres Aufenthalts.</p>",
    
    html: true,
  },
  "booking.gaestemappe.link_label": { de: "Digitale Gästemappe öffnen" },
  "booking.gaestemappe.link_aria": {
    de: "Digitale Gästemappe öffnen (öffnet in neuem Tab)",
    
  },
  "booking.gaestemappe.link_url": { de: "https://portal.gastfreund.net/aparthotel-lerch" },

  "booking.fruehstueck.title": { de: "FRÜHSTÜCK- & BRÖTCHENSERVICE" },
  "booking.fruehstueck.content": {
    de: "<p>Wählen Sie in unserer exklusiven Frühstücks‑App wahlweise ein komplettes Frühstück oder nur frische Brötchen und bestellen Sie bis 21:00 Uhr am Vortag. Wir liefern Ihre Auswahl morgens um ca. 7:00 Uhr direkt zu Ihrem Apartment.</p><p>Bitte beachten Sie, dass im Sommer sonn- und feiertags kein Frühstücks- oder Brötchenservice möglich ist.</p>",
    
    html: true,
  },

  "booking.haustiere.title": { de: "Haustiere" },
  "booking.haustiere.content": {
    de: "<p>Wir danken für Ihr Verständnis, dass Haustiere in den Lerch Suites nicht erlaubt sind.</p>",
    
    html: true,
  },

  "booking.rauchen.title": { de: "Rauchen & Ruhezeiten" },
  "booking.rauchen.content": {
    de: "<p>Das Rauchen ist nur im Freien oder am Balkon gestattet.</p><p>Aus Rücksicht auf all unsere Gäste bitten wir Sie, die Ruhezeiten von 22:00 bis 7:00 Uhr einzuhalten.</p>",
    
    html: true,
  },

  "booking.parken.title": { de: "Parken & Mobilität" },
  "booking.parken.content": {
    de: "<p><strong>Kostenfreie Parkplätze</strong> stehen Ihnen direkt vor unserem Haus zur Verfügung. Für Ihr Elektroauto steht eine <strong>E-Ladestation</strong> an unserem Haus zur Verfügung.</p><h4>Bushaltestelle</h4><p>Der Skibus Kappl hält direkt neben dem Haus, die Bushaltestelle für den Linienbus/Skibus Ischgl, Galtür und See ist ca. 400 m vom Haus entfernt. Es ist jeweils die Haltestelle „Kappl Weiler Wiese\". Skibus und Linienbus sind für unsere Gäste kostenlos nutzbar.</p>",
    
    html: true,
  },
  "booking.parken.link_skibus": { de: "Skibus" },
  "booking.parken.link_linienbus": { de: "Linienbusfahrplan" },
  "booking.parken.link_vvt_ios": { de: "Mobile Fahrplanauskunft in Tirol (VVT-App für iOS)" },
  "booking.parken.link_vvt_android": { de: "Mobile Fahrplanauskunft in Tirol (VVT-App für Android)" },
  "booking.parken.link_aria_skibus": { de: "Skibus Kappl (öffnet in neuem Tab)" },
  "booking.parken.link_aria_linienbus": { de: "Linienbusfahrplan ÖBB (öffnet in neuem Tab)" },
  "booking.parken.link_aria_vvt_ios": { de: "VVT-App für iOS (öffnet in neuem Tab)" },
  "booking.parken.link_aria_vvt_android": { de: "VVT-App für Android (öffnet in neuem Tab)" },
  "booking.parken.link_url_skibus": { de: "https://service.kappl.com/de/winter/skibus?affiliate=true" },
  "booking.parken.link_url_linienbus": { de: "https://fahrplan.oebb.at/bin/query.exe/dn" },
  "booking.parken.link_url_vvt_ios": { de: "https://apps.apple.com/kz/app/smartride/id459714443" },
  "booking.parken.link_url_vvt_android": { de: "https://play.google.com/store/apps/details?id=de.hafas.android.vvt" },

  "booking.fahrrad.title": { de: "Fahrradraum, Skiraum & -depot" },
  "booking.fahrrad.content": {
    de: "<p>Ihre Fahrräder können Sie in unserer Garage sicher abstellen. Bitte geben Sie uns bei Interesse vorab Bescheid.</p><p>Ein Skiraum mit abschließbaren Spinds und Schuhtrockner steht im Haus bereit. Zusätzlich besteht die bequeme Möglichkeit eines Skidepots bei Silvretta Sports in Ischgl.</p>",
    
    html: true,
  },
  "booking.fahrrad.link_silvretta": { de: "Silvretta Sports" },
  "booking.fahrrad.link_aria_silvretta": { de: "Silvretta Sports Skidepot (öffnet in neuem Tab)" },
  "booking.fahrrad.link_url_silvretta": { de: "https://silvrettasports.com/skidepot-ischgl/" },

  "booking.einkauf.title": { de: "Einkaufsservice" },
  "booking.einkauf.content": {
    de: "<p>Gerne befüllen wir Ihren Kühlschrank auf Anfrage vor Ihrer Anreise.</p>",
    
    html: true,
  },

  "booking.wellness.title": { de: "Wellnessbereich" },
  "booking.wellness.content": {
    de: "<p>Unser Wellnessbereich ist im Winter täglich von 16:00 bis 20:00 Uhr geöffnet.</p><p>Im Sommer: Nutzung auf Anfrage möglich.</p><p>Saunatücher liegen für Sie bereit.</p>",
    
    html: true,
  },

  "booking.gaestekarten.title": { de: "Gästekarten" },
  "booking.gaestekarten.content": {
    de: "<p><strong>Silvretta Card Premium</strong><br />Im Sommer (Ende Mai bis Mitte Oktober) erhalten Sie die Silvretta Card Premium ohne Aufpreis. Damit nutzen Sie zahlreiche Leistungen und Vorteile im Paznaun.</p><p><strong>Gästekarte Winter</strong><br />Mit der Gästekarte Kappl können Sie kostenlos den Bus Linie 260 zwischen Landeck Bahnhof und Galtür Wirl nutzen.</p>",
    
    html: true,
  },
  "booking.gaestekarten.link_aria_summer": { de: "Silvretta Card Premium (öffnet in neuem Tab)" },
  "booking.gaestekarten.link_aria_winter": { de: "Gästekarte Kappl im Winter (öffnet in neuem Tab)" },
  "booking.gaestekarten.link_url_summer": { de: "https://service.kappl.com/de/sommer/silvrettacardpremium?affiliate=true" },
  "booking.gaestekarten.link_url_winter": { de: "https://service.kappl.com/gaestekarte?affiliate=true" },

  // === Lage & Anreise ===
  "lage.seo.title": { de: "Lage & Anreise – Ihr Weg nach Kappl, Tirol" },
  "lage.seo.description": {
    de: "So erreichen Sie die Lerch Suites in Kappl im Paznaun: Anreise mit Auto, Bahn und Bus sowie Karte und Tipps für Ihre Anreise nach Tirol.",
    
  },
  "lage.hero.subline": { de: "Mitten in den Tiroler Alpen" },
  "lage.hero.headline": {
    de: "Ihr Weg zu den<br />Lerch Suites",
    
    html: true,
  },
  "lage.hero.image_alt": {
    de: "Ortsansicht von Kappl im Paznaun mit Kirche und Bergpanorama",
    
  },
  "lage.intro.body": {
    de: "Die Lerch Suites liegen <strong>zentral in Kappl</strong>, am Fuße des Paznauns. Unsere großzügigen und hochwertigen Apartments bieten somit den idealen Ausgangspunkt für Ihre Sommer- und Winterabenteuer. Hier finden Sie alle Informationen zur Anreise und eine interaktive Karte.",
    
    html: true,
  },

  // Auto
  "lage.auto.subline": { de: "Bequem auf vier Rädern" },
  "lage.auto.title": { de: "Anreise mit dem Auto" },
  "lage.auto.block.0.title": { de: "Aus Deutschland – über den Fernpass" },
  "lage.auto.block.0.text": { de: "Über den Fernpass nach Imst, dann Richtung Landeck/Arlberg. Nehmen Sie die Abfahrt Pians und biegen Sie ins Paznaun ab. Folgen Sie der Straße 14 km bis nach Kappl." },
  "lage.auto.block.1.title": { de: "Aus Deutschland – über Kufstein" },
  "lage.auto.block.1.text": { de: "Über Kufstein auf die Inntalautobahn Richtung Landeck/Arlberg. Nehmen Sie die Abfahrt Pians und biegen Sie ins Paznaun ab. Folgen Sie der Straße 14 km bis nach Kappl." },
  "lage.auto.block.2.title": { de: "Aus Deutschland – über den Bodensee" },
  "lage.auto.block.2.text": { de: "Über den Bodensee nach Feldkirch, dann über/durch den Arlberg. Nehmen Sie die Abfahrt Pians und biegen Sie ins Paznaun ab. Folgen Sie der Straße 14 km bis nach Kappl." },
  "lage.auto.block.3.title": { de: "Aus Italien – über den Brenner" },
  "lage.auto.block.3.text": { de: "Über den Brenner auf die Inntalautobahn Richtung Landeck/Arlberg. Nehmen Sie die Abfahrt Pians und biegen Sie ins Paznaun ab. Folgen Sie der Straße 14 km bis nach Kappl." },
  "lage.auto.block.4.title": { de: "Aus Italien – über den Reschenpass" },
  "lage.auto.block.4.text": { de: "Über den Reschenpass nach Nauders, dann weiter Richtung Landeck/Arlberg. Nehmen Sie die Abfahrt Pians und biegen Sie ins Paznaun ab. Folgen Sie der Straße 14 km bis nach Kappl." },
  "lage.auto.block.5.title": { de: "Aus der Schweiz – über Feldkirch" },
  "lage.auto.block.5.text": { de: "Über Feldkirch, weiter über/durch den Arlberg. Nehmen Sie die Abfahrt Pians und biegen Sie ins Paznaun ab. Folgen Sie der Straße 14 km bis nach Kappl." },
  "lage.auto.tip": {
    de: "<strong>Tipp:</strong> Ihr Auto können Sie kostenlos direkt vor unserem Haus parken.",
    
    html: true,
  },

  // Öffis
  "lage.oeffentlich.subline": { de: "Entspannt mit Öffis" },
  "lage.oeffentlich.title": { de: "Anreise mit Bus & Bahn" },
  "lage.oeffentlich.block.0.title": { de: "Nächstgelegene Bahnhöfe" },
  "lage.oeffentlich.block.0.text": { de: "Der nächstgelegene Bahnhof ist Landeck-Zams, etwa 20 km von den Lerch Suites entfernt. Alternativ können Sie auch den Bahnhof St. Anton am Arlberg nutzen." },
  "lage.oeffentlich.block.1.title": { de: "Bushaltestelle" },
  "lage.oeffentlich.block.1.text": { de: "Die Bushaltestelle „Kappl Weiler Wiese\" befindet sich direkt vor dem Haus." },
  "lage.oeffentlich.block.2.title": { de: "Von Landeck-Zams" },
  "lage.oeffentlich.block.2.text": { de: "Vom Bahnhof Landeck-Zams verkehren regelmäßig Busse ins Paznaun. Nehmen Sie die Buslinie 260 Richtung Ischgl und steigen Sie an der Haltestelle Kappl Abzweigung Wiese aus." },
  "lage.oeffentlich.block.3.title": { de: "Von St. Anton am Arlberg" },
  "lage.oeffentlich.block.3.text": { de: "Vom Bahnhof St. Anton am Arlberg können Sie mit dem Taxi oder einem Transferservice zu den Lerch Suites in Kappl fahren (ca. 30 km)." },
  "lage.oeffentlich.tip": {
    de: "<strong>Tipp:</strong> Gerne sind wir Ihnen bei der Organisation eines Transfers von den Bahnhöfen zu den Lerch Suites behilflich. Kontaktieren Sie uns einfach vor Ihrer Anreise.",
    
    html: true,
  },
  "lage.oeffentlich.cta.0_label": { de: "Skibus" },
  "lage.oeffentlich.cta.0_aria": { de: "Skibus Kappl (öffnet in neuem Tab)" },
  "lage.oeffentlich.cta.1_label": { de: "Fahrplanauskunft" },
  "lage.oeffentlich.cta.1_aria": { de: "Fahrplanauskunft ÖBB (öffnet in neuem Tab)" },
  "lage.oeffentlich.cta.2_label": { de: "VVT-App für iOS" },
  "lage.oeffentlich.cta.2_aria": { de: "VVT-App für iOS (öffnet in neuem Tab)" },
  "lage.oeffentlich.cta.3_label": { de: "VVT-App für Android" },
  "lage.oeffentlich.cta.3_aria": { de: "VVT-App für Android (öffnet in neuem Tab)" },
  "lage.oeffentlich.cta.0_url": { de: "https://service.kappl.com/de/winter/skibus?affiliate=true" },
  "lage.oeffentlich.cta.1_url": { de: "https://fahrplan.oebb.at/bin/query.exe/dn" },
  "lage.oeffentlich.cta.2_url": { de: "https://apps.apple.com/kz/app/smartride/id459714443" },
  "lage.oeffentlich.cta.3_url": { de: "https://play.google.com/store/apps/details?id=de.hafas.android.vvt" },

  "lage.cta_book": { de: "Jetzt buchen" },

  // Karte
  "lage.karte.subline": { de: "So finden Sie uns" },
  "lage.karte.title": { de: "Karte" },
  "lage.karte.iframe_title": { de: "Karte – Lerch Suites in Kappl" },
  "lage.karte.osm_link": { de: "Größere Karte auf OpenStreetMap anzeigen" },
  "lage.karte.osm_link_aria": { de: "Größere Karte auf OpenStreetMap anzeigen (öffnet in neuem Tab)" },
  "lage.karte.osm_link_url": { de: "https://www.openstreetmap.org/?mlat=47.0546351&mlon=10.3594047#map=16/47.0546351/10.3594047" },

  // === Lerch Plus – Seite ===
  "lerch_plus.seo.title": { de: "Ferienwohnungen Kappl | Lerch Plus – unser Service für mehr Komfort" },
  "lerch_plus.hero.kicker": { de: "Flexibel, unkompliziert, durchdacht" },
  "lerch_plus.hero.headline": {
    de: "Lerch Plus –<br /> Ihr Komfort ist unser Service",
    
    html: true,
  },
  "lerch_plus.intro": {
    de: "Lerch Plus fasst alle Zusatzleistungen zusammen,<br />die Ihren Aufenthalt in unseren großzügigen Lerch Suites noch <strong>komfortabler und angenehmer</strong> machen. Wir – Ihre Gastgeberfamilie Rebecca und Ramiro Lerch – sind <strong>persönlich für Sie da</strong> und sorgen mit viel Herzlichkeit, flexiblem Service und der praktischen Frühstücks-App für entspannte Urlaubstage&nbsp;in&nbsp;Kappl.",
    
    html: true,
  },
  "lerch_plus.quote.text": {
    de: "„Als Gastgeberfamilie ist es uns wichtig, dass Sie sich rundum wohlfühlen und Ihren Urlaub ganz unbeschwert genießen können.\"",
    
  },
  "lerch_plus.quote.author": { de: "— Ihre Gastgeberfamilie Lerch" },

  "lerch_plus.gaestemappe.kicker": { de: "Alles auf einen Klick" },
  "lerch_plus.gaestemappe.headline": { de: "Digitale Gästemappe mit Frühstücks-App" },
  "lerch_plus.gaestemappe.body": {
    de: "Mit unserer digitalen Gästemappe erhalten Sie alle Informationen rund um Ihren Aufenthalt sowie ausgewählte Tipps für die Region.<br /><br />Wählen Sie in unserer exklusiven Frühstücks-App wahlweise ein komplettes Frühstück oder nur frische Brötchen und bestellen Sie bis 21:00 Uhr am Vortag. Wir liefern Ihre Auswahl morgens um ca. 7:00 Uhr direkt zu Ihrem Apartment – einfach, flexibel und ohne festen Ablauf. Die Bestellung erfolgt digital inklusive Bezahlmöglichkeit, der Zugang erfolgt einfach über QR-Code.",
    
    html: true,
  },
  "lerch_plus.gaestemappe.image_alt": { de: "Digitale Gästemappe mit Frühstücks-App" },

  "lerch_plus.extras.kicker": { de: "Für einen noch angenehmeren Urlaub" },
  "lerch_plus.extras.headline": { de: "Die Lerch Suites Extras" },
  "lerch_plus.extras.intro": {
    de: "Neben den standardmäßigen Annehmlichkeiten unserer großzügigen Apartments bieten die Lerch Suites <strong>zusätzliche Services und Extras</strong>, die Ihren Aufenthalt noch komfortabler und unkomplizierter gestalten. Diese durchdachten Details sorgen dafür, dass Sie sich rundum wohlfühlen und Ihren Urlaub ganz entspannt genießen können.",
    
    html: true,
  },
  "lerch_plus.extras.prev_aria": { de: "Vorheriges Extra" },
  "lerch_plus.extras.next_aria": { de: "Nächstes Extra" },
  "lerch_plus.extras.dot_aria": { de: "Extra anzeigen" },

  "lerch_plus.extras.0.kicker": { de: "Für die Erfrischung zwischendurch" },
  "lerch_plus.extras.0.headline": { de: "Getränkekühlschrank zur Selbstbedienung" },
  "lerch_plus.extras.0.body": {
    de: "Nutzen Sie unseren Getränkekühlschrank und bedienen Sie sich ganz flexibel während Ihres Aufenthalts. Eine einfache und praktische Möglichkeit, sich jederzeit mit Getränken zu versorgen.",
    
  },
  "lerch_plus.extras.0.alt": { de: "Getränkekühlschrank zur Selbstbedienung" },

  "lerch_plus.extras.1.kicker": { de: "Für Ihren Familienurlaub" },
  "lerch_plus.extras.1.headline": { de: "Kinderspielplatz" },
  "lerch_plus.extras.1.body": {
    de: "Für unsere kleinen Gäste steht ein neuer Spielplatz zur Verfügung. Ein Ort, an dem Kinder sich frei bewegen, spielen und die Zeit im Freien genießen können.",
    
  },
  "lerch_plus.extras.1.alt": { de: "Kinderspielplatz" },

  "lerch_plus.extras.2.kicker": { de: "Bequem laden während Ihres Aufenthalts" },
  "lerch_plus.extras.2.headline": { de: "E-Ladestationen" },
  "lerch_plus.extras.2.body": {
    de: "Für Ihr Elektroauto stehen Ihnen bei den Lerch Suites E-Ladestationen direkt vor Ort zur Verfügung. So bleiben Sie auch im Urlaub flexibel und jederzeit mobil.",
    
  },
  "lerch_plus.extras.2.alt": { de: "E-Ladestationen" },

  "lerch_plus.extras.3.kicker": { de: "Alles gut untergebracht" },
  "lerch_plus.extras.3.headline": { de: "Skiraum & Bike-Abstellraum" },
  "lerch_plus.extras.3.body": {
    de: "Im Haus steht Ihnen ein Skiraum mit abschließbaren Spints und Skischuhtrockner zur Verfügung. Für zusätzlichen Komfort beim Skifahren empfehlen wir, das [SKIDEPOT_LINK] in Ischgl zu nutzen.<br /><br />Fahrräder können sicher in unserer Garage untergebracht werden. Steckdosen zum Laden von E-Bikes sind vorhanden. Bitte geben Sie uns bei Interesse vorab Bescheid.",
    
    html: true,
  },
  "lerch_plus.extras.3.alt": { de: "Skiraum & Bike-Abstellraum" },
  "lerch_plus.extras.3.link_label": { de: "Skidepot von Silvretta Sports" },
  "lerch_plus.extras.3.link_url": { de: "https://silvrettasports.com/skidepot-ischgl/" },

  "lerch_plus.silvretta.kicker": { de: "Ihre Vorteile im Sommer" },
  "lerch_plus.silvretta.headline": { de: "Silvretta Card Premium Partner" },
  "lerch_plus.silvretta.body": {
    de: "Als Gast der Lerch Suites erhalten Sie die <strong>Silvretta Card Premium ohne Aufpreis</strong>. Damit nutzen Sie zahlreiche Leistungen, kostenlose Eintritte und Vorteile in der Region – von Bergbahnen bis zu ausgewählten Attraktionen.",
    
    html: true,
  },
  "lerch_plus.silvretta.cta_label": { de: "Vorteile der Silvretta Card" },
  "lerch_plus.silvretta.cta_aria": { de: "Vorteile der Silvretta Card Premium (öffnet in neuem Tab)" },
  "lerch_plus.silvretta.cta_url": { de: "https://service.ischgl.com/de/sommer/silvrettacardpremium?affiliate=true" },
  "lerch_plus.silvretta.card_alt": { de: "Silvretta Card Premium" },

  "lerch_plus.lage.kicker": { de: "Alles in Reichweite" },
  "lerch_plus.lage.headline": { de: "Lage" },
  "lerch_plus.lage.p1": {
    de: "Die Lerch Suites liegen <strong>ruhig in Kappl</strong> und gleichzeitig in der Nähe von Ischgl. Der Skibus Kappl hält direkt vor dem Haus. Der Skibus nach Ischgl sowie der Talbus nach Galtür und See sind in wenigen Minuten erreichbar (ca. 400 m).",
    
    html: true,
  },
  "lerch_plus.lage.p2": {
    de: "Das Dorfzentrum und der nächste Supermarkt liegen rund 2 km entfernt und sind in etwa 5 Minuten mit dem Auto erreichbar. <strong>Kostenfreie Parkplätze</strong> stehen direkt bei der Unterkunft zur Verfügung.",
    
    html: true,
  },
  "lerch_plus.lage.image_alt": { de: "Lage der Lerch Suites in Kappl" },

  "lerch_plus.restaurants.kicker": { de: "Persönlich für Sie ausgewählt" },
  "lerch_plus.restaurants.headline": { de: "Exklusive Restaurant-Tipps" },
  "lerch_plus.restaurants.body": {
    de: "Gerne empfehlen wir Ihnen <strong>ausgewählte Restaurants in der Umgebung</strong> – sorgfältig für Sie ausgewählt, damit Sie Ihren Urlaub auch kulinarisch unkompliziert, aber hochwertig genießen können.",
    
    html: true,
  },
  "lerch_plus.restaurants.post_alt": { de: "Hotel Post in Kappl" },
  "lerch_plus.restaurants.mountaingarage_alt": { de: "Mountaingarage in Kappl" },
  "lerch_plus.restaurants.salnerhof_alt": { de: "Salnerhof in Ischgl" },
  "lerch_plus.restaurants.kebap_alt": { de: "Kebap Corner Kappl – mit Lieferservice" },
  "lerch_plus.restaurants.trattoria_alt": { de: "Trattoria in Ischgl" },
  "lerch_plus.restaurants.nevada_alt": { de: "Nevada in Ischgl" },

  // === Erlebnisse – Übersicht ===
  "erlebnisse.seo.title": { de: "Urlaub in Kappl | Familienurlaub | Skiurlaub" },
  "erlebnisse.hero.subline": { de: "Sommer- & Wintererlebnisse" },
  "erlebnisse.hero.headline": { de: "Ihr Abenteuer in den Tiroler Alpen" },
  "erlebnisse.hero.image_alt": { de: "Erlebnisse in Kappl & Paznaun" },
  "erlebnisse.intro.body": {
    de: "Kappl und das Paznaun bieten das ganze Jahr über<br /><strong>abwechslungsreiche Erlebnisse</strong> für Familien, aktive Gäste und alle, die Erholung in den Bergen suchen. Entdecken Sie die Vielfalt der Region und gestalten Sie Ihren Urlaub ganz nach Ihren Vorstellungen. Ob <strong>Familien-, Sommer- oder Skiurlaub</strong> – Kappl&nbsp;kann&nbsp;alles!",
    
    html: true,
  },
  "erlebnisse.intro.cta": { de: "Jetzt buchen" },

  "erlebnisse.sommer.subline": { de: "Zeit für Familien, Natur\nund gemeinsame Erlebnisse" },
  "erlebnisse.sommer.headline": { de: "Sommer in\nKappl & Paznaun" },
  "erlebnisse.sommer.p1": {
    de: "Grüne Wiesen, <strong>kristallklare Bergseen</strong> und Gipfel, die den Himmel berühren. Der Sommer in Kappl und im Paznaun hält vielseitige Möglichkeiten für Sie bereit.",
    
    html: true,
  },
  "erlebnisse.sommer.p2": {
    de: "Ob <strong>Wandern, Mountainbiken</strong> oder der Sunny Mountain Erlebnispark für Familien – die Region bietet abwechslungsreiche Aktivitäten für spannende Tage und entspannte Stunden. Auch bei wechselhaftem Wetter sorgen Ausflugsziele und Thermenangebote in der näheren und weiteren Region für passende Alternativen.",
    
    html: true,
  },
  "erlebnisse.sommer.cta_label": { de: "Mehr zum Sommer" },
  "erlebnisse.sommer.image_alt": { de: "Sommer in Kappl und Paznaun" },

  "erlebnisse.winter.subline": { de: "Aktive Tage im Schnee" },
  "erlebnisse.winter.headline": { de: "Winter in Kappl & Paznaun" },
  "erlebnisse.winter.text": {
    de: "Verschneite Landschaften, bestens präparierte Pisten und klare Bergluft prägen Ihren Winterurlaub in Kappl und im Paznaun. In der <strong>Silvretta Arena Ischgl</strong> sowie in den <strong>Familienskigebieten in Kappl und See</strong> erleben Sie abwechslungsreiche Skitage in einer der schönsten Skiregionen Österreichs. Auch abseits der Piste laden <strong>Langlaufen und Winterwandern</strong> dazu ein, die winterliche Bergwelt in ruhiger Atmosphäre zu erleben.",
    
    html: true,
  },
  "erlebnisse.winter.cta_label": { de: "Mehr zum Winter" },
  "erlebnisse.winter.image_alt": { de: "Winter in Kappl & Paznaun" },

  "erlebnisse.events.kicker": { de: "Veranstaltungen und besondere Momente" },
  "erlebnisse.events.headline": { de: "Events &\nHighlights" },
  "erlebnisse.events.text": {
    de: "Informieren Sie sich über <strong>aktuelle Events, Feste und Highlights</strong> in Kappl, Ischgl und im gesamten Paznaun und verpassen Sie keine Highlights aus der Region.",
    
    html: true,
  },
  "erlebnisse.events.cta_label": { de: "Mehr zu Events" },
  "erlebnisse.events.tile_sommer": { de: "Sommer-Events" },
  "erlebnisse.events.tile_highlights": { de: "Highlights" },
  "erlebnisse.events.tile_winter": { de: "Winter-Events" },

  // === Top Events – Seite ===
  "topevents.seo.title": { de: "Urlaub in Kappl | Top Events | stilvolle Apartments" },
  "topevents.hero.subline": { de: "Die Highlights der Region" },
  "topevents.hero.headline": { de: "Top-Events\ndas ganze Jahr über" },
  "topevents.hero.image_alt": { de: "Top-Events im Paznaun" },
  "topevents.intro.body": {
    de: "Erleben Sie das ganze Jahr über <strong>Top Events</strong><br />in Kappl, Ischgl und Umgebung. Sowohl im Winter als auch im Sommer präsentiert Ihnen die Region mitreißende Veranstaltungen aus den Bereichen <strong>Musik & Sport</strong>. Informieren Sie sich bei uns über die aktuellen Veranstaltungen, damit Sie während Ihres Aufenthalts keine&nbsp;Highlights&nbsp;verpassen.",
    
    html: true,
  },
  "topevents.intro.cta_label": { de: "Zu den Top-Events" },
  "topevents.intro.cta_aria": { de: "Zu den Top-Events in Kappl (öffnet in neuem Tab)" },
  "topevents.intro.cta_url": { de: "https://www.ischgl.com/de/veranstaltungen-erlebnisse/topevents?affiliate=true" },

  "topevents.winter.subline": { de: "Eiskalte Events" },
  "topevents.winter.headline": { de: "Winter-veranstaltungen" },
  "topevents.winter.body": {
    de: "Die Wintermonate bieten <strong>zahlreiche Events und Highlights</strong> in der Region, ideal kombiniert mit einem entspannten Aufenthalt in den Lerch Suites.",
    
    html: true,
  },
  "topevents.winter.image_alt": { de: "Winterveranstaltungen im Paznaun" },

  "topevents.sommer.kicker": { de: "Bergeweise Erlebnisse" },
  "topevents.sommer.headline": { de: "Sommer\u00ADhighlights" },
  "topevents.sommer.body": {
    de: "Die Sommermonate bringen ein <strong>abwechslungsreiches Veranstaltungsprogramm</strong> nach Kappl, Ischgl und in die Region – perfekt für Familien und aktive Gäste.",
    
    html: true,
  },
  "topevents.sommer.tile_1": { de: "Sommerhighlights im Paznaun" },
  "topevents.sommer.tile_2": { de: "Sommer-Event" },
  "topevents.sommer.tile_3": { de: "Event-Highlights" },

  "topevents.popup.winter_label": { de: "Winter-Events" },
  "topevents.popup.winter_title": { de: "Winter-Events" },
  "topevents.popup.winter.0": { de: "Veranstaltungskalender Kappl" },
  "topevents.popup.winter.1": { de: "Veranstaltungskalender Ischgl" },
  "topevents.popup.winter.3": { de: "Watersurf Contest" },
  "topevents.popup.winter.4": { de: "Osterparty" },
  "topevents.popup.winter.5": { de: "Spring Blanc" },
  "topevents.popup.winter.7": { de: "Ischgl Trophy" },
  "topevents.popup.winter.0_url": { de: "https://www.kappl-see.com/de/live-infos/topevents?affiliate=true" },
  "topevents.popup.winter.1_url": { de: "https://www.ischgl.com/de/veranstaltungen-erlebnisse/veranstaltungskalender?affiliate=true" },
  "topevents.popup.winter.3_url": { de: "https://www.kappl-see.com/de/veranstaltungen-erlebnisse/topevents/watersurf-contest-sunset-beats-auf-der-medrig-alm_event_39852559?affiliate=true" },
  "topevents.popup.winter.4_url": { de: "https://www.kappl-see.com/de/veranstaltungen-erlebnisse/topevents/osterparty-in-see_event_49705937?affiliate=true" },
  "topevents.popup.winter.5_url": { de: "https://www.ischgl.com/de/springblanc?affiliate=true" },
  "topevents.popup.winter.7_url": { de: "https://www.ischgl.com/de/veranstaltungen-erlebnisse/topevents/ischgl-trophy_event_120333679?affiliate=true" },

  "topevents.popup.sommer_label": { de: "Sommer-Events" },
  "topevents.popup.sommer_title": { de: "Sommer-Events" },
  "topevents.popup.sommer.0": { de: "PIUT" },
  "topevents.popup.sommer.1": { de: "E-Bike-WM" },
  "topevents.popup.sommer.2": { de: "Ironbike" },
  "topevents.popup.sommer.3": { de: "Sunny Mountain Sommerfest" },
  "topevents.popup.sommer.4": { de: "Fest am Berg" },
  "topevents.popup.sommer.5": { de: "Silvretta Ferwall Marsch" },
  "topevents.popup.sommer.6": { de: "Paznauner Markttag" },
  "topevents.popup.sommer.0_url": { de: "https://www.paznaun-ischgl.com/piut" },
  "topevents.popup.sommer.1_url": { de: "https://service.kappl.com/de/veranstaltungen-erlebnisse/topevents/e-bike-wm-fuer-jedermann_event_39624189?affiliate=true" },
  "topevents.popup.sommer.2_url": { de: "https://www.ischgl.com/de/veranstaltungen-erlebnisse/ischgl-ironbike?affiliate=true" },
  "topevents.popup.sommer.3_url": { de: "https://www.kappl-see.com/de/live-infos/topevents/sunny-mountain-sommerfest_event_22006959?affiliate=true" },
  "topevents.popup.sommer.4_url": { de: "https://www.kappl-see.com/de/veranstaltungen-erlebnisse/topevents/fest-am-berg-in-see_event_53586135?affiliate=true" },
  "topevents.popup.sommer.5_url": { de: "https://www.kappl-see.com/de/veranstaltungen-erlebnisse/topevents/silvretta-ferwall-marsch_event_40040825?affiliate=true" },
  "topevents.popup.sommer.6_url": { de: "https://www.kappl-see.com/de/veranstaltungen-erlebnisse/topevents/17-paznauner-markttag_event_43041801?affiliate=true" },

  // === Erlebnisse Sommer ===
  "sommer.seo.title": { de: "Urlaub in Kappl | Wandern & Biken | stilvolle Apartments" },
  "sommer.hero.subline": { de: "Wandern, Biken & Familienabenteuer" },
  "sommer.hero.headline": { de: "Unvergesslicher Bergsommer" },
  "sommer.hero.image_alt": { de: "Sommer im Paznaun" },
  "sommer.intro.body": {
    de: "Der Sommer in Kappl und im Paznaun bietet<br />ideale Bedingungen für einen <strong>abwechslungsreichen Familienurlaub</strong> mit Action und Abenteuern. Ob gemütliche Wanderungen, erste Bike-Erlebnisse oder gemeinsame Ausflüge in die Natur – erleben Sie traumhafte Tage und erholsame Nächte bei uns in den Lerch Suites. Als <strong>Partnerbetrieb der Silvretta Card Premium</strong> bieten wir Ihnen zahlreiche inkludierte Leistungen. Nutzen Sie die Angebote der Region ganz unkompliziert und freuen Sie sich auf einen&nbsp;tollen&nbsp;Urlaub.",
    
    html: true,
  },

  "sommer.wandern.subline": { de: "Vielfältige Touren für die ganze Familie" },
  "sommer.wandern.headline": { de: "Wandern im\nPaznaun" },
  "sommer.wandern.body": {
    de: "Entdecken Sie über 75 Routen und rund 1.000 Kilometer Wanderwege im Paznaun. Highlights wie der <strong>Paznauner Höhenweg</strong> oder der <strong>Kulinarische Jakobsweg</strong> bieten abwechslungsreiche Touren für unterschiedliche Ansprüche. Auch für Familien bietet die Region zahlreiche Wandermöglichkeiten, traumhafte Aussichten inklusive!",
    
    html: true,
  },
  "sommer.wandern.cta": { de: "Wanderwege entdecken" },
  "sommer.wandern.cta_url": { de: "https://www.ischgl.com/de/sommer/wandern?affiliate=true" },
  "sommer.wandern.image_alt": { de: "Wandern im Paznaun" },

  "sommer.biken.subline": { de: "Trailspaß für jedes Niveau" },
  "sommer.biken.headline": { de: "Biken" },
  "sommer.biken.body": {
    de: "In Kappl und im gesamten Paznaun finden Sie <strong>vielfältige Möglichkeiten zum Biken</strong> – von gemütlichen Familienradtouren bis hin zu anspruchsvollen Trails. Die <strong>Silva Trails in Galtür</strong> bieten auf rund 15 Kilometern abwechslungsreiche Strecken für Newcomer und erfahrene Bike- und E-Bike-Fans. Von Flowtrails und Jumplines bis hin zu Pumptracks und technisch anspruchsvolleren Abschnitten ist für jedes Niveau etwas dabei. Auch Familien profitieren von der vielseitigen Trail-Landschaft.",
    
    html: true,
  },
  "sommer.biken.cta": { de: "Silva Trails Galtür" },
  "sommer.biken.cta_url": { de: "https://www.galtuer.com/de/sommer/biken?affiliate=true" },
  "sommer.biken.image_alt": { de: "Biken im Paznaun" },

  "sommer.sunny.kicker": { de: "Familienerlebnis in den Bergen" },
  "sommer.sunny.headline": { de: "Sunny Mountain Erlebnispark" },
  "sommer.sunny.body": {
    de: "Der Sunny Mountain Erlebnispark in Kappl ist ein beliebtes Sommer-Ausflugsziel für Familien auf über 1.800 Metern Seehöhe. Nach der Auffahrt mit der Diasbahn erwartet Sie ein vielseitiger Erlebnisbereich mit Rutschturm, Streichelzoo, Kneippanlage und weiteren Attraktionen. Zusätzlich sorgen die <strong>Sunny Mountain Carts</strong> mit einer abwechslungsreichen Strecke ins Tal für ein weiteres Highlight. Mit der Silvretta Card Premium sind Bergbahn und Eintritt bereits inkludiert.",
    
    html: true,
  },
  "sommer.sunny.cta": { de: "Mehr zum Sunny Mountain" },
  "sommer.sunny.cta_url": { de: "https://www.kappl-see.com/de/sommer/familien-attraktionen/sunny-mountain" },
  "sommer.sunny.tile1": { de: "Sunny Mountain" },
  "sommer.sunny.tile2": { de: "Sunny Mountain Erlebnispark" },
  "sommer.sunny.tile3": { de: "Family Paradise Kappl" },

  "sommer.silvretta_card.kicker": { de: "Ihre Vorteile im Sommer" },
  "sommer.silvretta_card.headline": { de: "Silvretta Card Premium Partner" },
  "sommer.silvretta_card.body": {
    de: "Als Gast der Lerch Suites profitieren Sie von der <strong>Silvretta Card Premium</strong>, die Ihnen zahlreiche Vorteile und kostenlose Eintritte zu Attraktionen in der Region bietet. Dazu gehören unter anderem die geöffneten Bergbahnen in Kappl, Ischgl, Galtür und See, die Regiobusse im Paznaun sowie die Silvretta Hochalpenstraße. So gestalten Sie Ihren Sommerurlaub <strong>flexibel und unkompliziert</strong>.",
    
    html: true,
  },
  "sommer.silvretta_card.cta": { de: "Vorteile der Silvretta Card" },
  "sommer.silvretta_card.cta_url": { de: "https://www.ischgl.com/de/sommer/silvrettacardpremium?affiliate=true" },

  "sommer.therme.subline": { de: "Wellness & Entspannung" },
  "sommer.therme.headline": { de: "Silvretta Therme" },
  "sommer.therme.body": {
    de: "Die Silvretta Therme in Ischgl bietet eine <strong>großzügige Wasser-</strong> und Saunalandschaft mit Innen- und Außenpools, verschiedenen Saunen und ruhigen Rückzugsbereichen. Ergänzt wird das Angebot durch ein Sportbecken und moderne Infrastruktur – ideal für entspannte Stunden nach einem aktiven Tag in den Bergen.",
    
    html: true,
  },
  "sommer.therme.cta": { de: "Mehr zur Silvretta Therme" },
  "sommer.therme.cta_url": { de: "https://www.silvrettatherme.at/de" },
  "sommer.therme.image_alt": { de: "Silvretta Therme in Ischgl" },

  "sommer.schlecht.kicker": { de: "Bei jedem Wetter" },
  "sommer.schlecht.headline": { de: "Ausflugsziele in Tirol" },
  "sommer.schlecht.body": {
    de: "Auch bei Regenwetter haben das Paznaun und die weitere Umgebung in Tirol viel zu bieten: Verbringen Sie einen entspannten Tag in der Therme, genießen Sie einen Ausflug in ein <strong>Museum</strong>, verbringen Sie einen Nachmittag in einem <strong>familienfreundlichen Indoor Spielplatz</strong> oder gehen Sie <strong>shoppen</strong>. Die großzügigen Apartments in den Lerch Suites erwarten Sie anschließend, um den Tag ruhig ausklingen zu lassen.",
    
    html: true,
  },
  "sommer.schlecht.cta": { de: "Ausflugsziele erkunden" },
  "sommer.schlecht.dialog_title": { de: "Ausflugsziele erkunden" },
  "sommer.schlecht.link.0": { de: "Shopping in Innsbruck" },
  "sommer.schlecht.link.1": { de: "Alpinarium Galtür" },
  "sommer.schlecht.link.2": { de: "Joy Kinderparadies in Völs" },
  "sommer.schlecht.link.3": { de: "Shopping im FMZ Imst" },
  "sommer.schlecht.link.4": { de: "Playtopia im FMZ Imst" },
  "sommer.schlecht.link.5": { de: "Swarovski Kristallwelten in Wattens" },
  "sommer.schlecht.link.6": { de: "Trampolin & mehr im Arl.Park St. Anton" },
  "sommer.schlecht.link.0_url": { de: "https://www.innsbruck.info/shopping.html" },
  "sommer.schlecht.link.1_url": { de: "https://www.alpinarium.at/" },
  "sommer.schlecht.link.2_url": { de: "https://joy-daskinderparadies.at/" },
  "sommer.schlecht.link.3_url": { de: "https://www.fmz-imst.at/" },
  "sommer.schlecht.link.4_url": { de: "https://playtopia-imst.at/" },
  "sommer.schlecht.link.5_url": { de: "https://www.kristallwelten.swarovski.com/de" },
  "sommer.schlecht.link.6_url": { de: "https://arlpark.com/trampolin/" },
  "sommer.schlecht.tile1": { de: "Schlechtwetterprogramm" },
  "sommer.schlecht.tile2": { de: "Ausflugsziele erkunden" },
  "sommer.schlecht.tile3": { de: "Trampolinpark arl.park" },

  // === Erlebnisse Winter ===
  "winter.seo.title": { de: "Urlaub in Kappl | Skifahren in Ischgl & Kappl | stilvolle Apartments" },
  "winter.hero.subline": { de: "Skifahren, Snowboarden & mehr" },
  "winter.hero.headline": { de: "Winterzauber\nin den Alpen" },
  "winter.hero.image_alt": { de: "Winter im Paznaun" },
  "winter.intro.body": {
    de: "Erleben Sie einen <strong>unvergesslichen Winterurlaub</strong><br />in Kappl und im Paznaun. Von den perfekt präparierten Pisten der <strong>Silvretta Arena Ischgl</strong> über idyllische Winterwanderwege abseits des Trubels und weitere Aktivitäten – hier kommt jeder auf&nbsp;seine&nbsp;Kosten.",
    
    html: true,
  },

  "winter.arena.subline": { de: "Skifahren & Snowboarden deluxe" },
  "winter.arena.headline": { de: "Die Silvretta\nArena Ischgl" },
  "winter.arena.body": {
    de: "Nur wenige Minuten von Kappl entfernt liegt die Silvretta Arena Ischgl/Samnaun, eines der größten Skigebiete der Alpen. 239 bestens präparierte Pistenkilometer, modernste Liftanlagen und <strong>Schneesicherheit von November bis Mai</strong> garantieren puren Skigenuss und sorgen für abwechslungsreiche Tage auf der Piste.",
    
    html: true,
  },
  "winter.arena.cta1": { de: "Silvretta Arena" },
  "winter.arena.cta2": { de: "Tickets & Preise" },
  "winter.arena.cta1_url": { de: "https://www.ischgl.com/de/winter/silvretta-arena?affiliate=true" },
  "winter.arena.cta2_url": { de: "https://www.ischgl.com/de/winter/betriebszeiten-preise/skipasspreise-silvretta?affiliate=true" },
  "winter.arena.image_alt": { de: "Silvretta Arena Ischgl" },

  "winter.skischaukel.subline": { de: "Familienspaß inklusive" },
  "winter.skischaukel.headline": { de: "Skischaukel Kappl & See" },
  "winter.skischaukel.body": {
    de: "Mit nur einem Skipass nutzen Sie in der Skischaukel Kappl & See <strong>über 80 Pistenkilometer</strong> – von sonnigen Abfahrten bis hin zu sportlicheren Strecken und eigenen Bereichen für Kinder. Beide Orte sind als Familienskigebiete perfekt für die ersten Schwünge im Schnee. Aber auch abseits der präparierten Pisten bietet Kappl Möglichkeiten für Freeride-Erlebnisse in gesichertem Gelände.",
    
    html: true,
  },
  "winter.skischaukel.cta1": { de: "Skigebiet Kappl" },
  "winter.skischaukel.cta2": { de: "Tickets & Preise" },
  "winter.skischaukel.cta1_url": { de: "https://www.kappl-see.com/de/winter?affiliate=true" },
  "winter.skischaukel.cta2_url": { de: "https://www.kappl-see.com/de/tickets-preise/skipasspreise-betriebszeiten?affiliate=true" },
  "winter.skischaukel.image_alt": { de: "Skischaukel Kappl & See" },

  "winter.sunny.kicker": { de: "Spielerisch die ersten Schwünge im Schnee" },
  "winter.sunny.headline": { de: "Sunny Mountain Erlebnispark im Winter" },
  "winter.sunny.body": {
    de: "Auch im Winter bietet der Sunny Mountain Erlebnispark in Kappl Spaß und Action für Kinder. Auf <strong>anfängergerechten Übungshängen mit Zauberteppich</strong> können hier die Kleinsten spielerisch ihre ersten Erfahrungen auf Skiern sammeln – perfekt für Ihren Familienurlaub im Paznaun.",
    
    html: true,
  },
  "winter.sunny.cta": { de: "Mehr zum Sunny Mountain" },
  "winter.sunny.cta_url": { de: "https://www.kappl-see.com/de/winter/familienwinter?affiliate=true" },

  "winter.rodeln.subline": { de: "Spaß abseits der Piste" },
  "winter.rodeln.headline": { de: "Rodeln, Winterwandern & Langlaufen" },
  "winter.rodeln.body": {
    de: "Erleben Sie die verschneite Winterlandschaft des Paznauntals bei <strong>Winterwanderungen</strong>, auf den Langlaufloipen oder bei <strong>einer Rodelpartie</strong>. Abseits des Skibetriebs finden Sie in Kappl und Umgebung Ruhe und genießen die Natur in einer besonders entspannten Atmosphäre.",
    
    html: true,
  },
  "winter.rodeln.cta1": { de: "Abseits der Piste" },
  "winter.rodeln.cta2": { de: "Langlaufen" },
  "winter.rodeln.cta1_url": { de: "https://www.kappl-see.com/de/winter/abseits-der-piste?affiliate=true" },
  "winter.rodeln.cta2_url": { de: "https://www.galtuer.com/de/winter/langlaufen?affiliate=true" },
  "winter.rodeln.image_alt": { de: "Rodeln & Winterwandern im Paznaun" },

  "winter.therme.kicker": { de: "Genuss & Entspannung" },
  "winter.therme.headline": { de: "Silvretta Therme" },
  "winter.therme.body": {
    de: "Die Silvretta Therme in Ischgl bietet eine moderne <strong>Wasser-</strong> und Saunalandschaft mit Innen- und Außenpools sowie großzügigen Ruhebereichen. Ideal, um nach einem aktiven Tag im Schnee bewusst abzuschalten und neue Energie zu sammeln.",
    
    html: true,
  },
  "winter.therme.cta": { de: "Mehr zur Silvretta Therme" },
  "winter.therme.cta_url": { de: "https://www.silvrettatherme.at/de" },
  "winter.therme.tile1": { de: "Silvretta Therme" },
  "winter.therme.tile2": { de: "Wasserlandschaft Silvretta Therme" },
  "winter.therme.tile3": { de: "Saunalandschaft Silvretta Therme" },

  // === Suites Overview ===
  "suites.overview.headline_fallback": { de: "Großzügige Apartments" },
  "suites.overview.subline_fallback": { de: "für unvergessliche Urlaubsmomente" },
  "suites.overview.intro_fallback": {
    de: "Entdecken Sie die Lerch Suites – <strong>hochwertig ausgestattet</strong>, modern eingerichtet<br />und mit viel Raum zum Ankommen und Wohlfühlen. Durchdachte Details und ein klares Design schaffen den passenden Rahmen für Ihren <strong>Urlaub in Kappl</strong>, Sommer wie Winter. Jedes Apartment ist ein Ort, an dem Sie zur Ruhe kommen und Ihre Zeit entspannt&nbsp;genießen&nbsp;können.",
    
  },
  "suites.overview.persons_range": { de: "{min} – {max} Personen" },
  "suites.overview.details": { de: "Details" },
  "suites.overview.inquire": { de: "Anfragen" },
  "suites.overview.book": { de: "Buchen" },
  "suites.overview.ausstattung_kicker": { de: "Alles für Ihren komfortablen Aufenthalt" },
  "suites.overview.ausstattung_headline": { de: "Ausstattung" },
  "suites.overview.ausstattung.item.0": { de: "Bettwäsche" },
  "suites.overview.ausstattung.item.1": { de: "Kleiderschrank" },
  "suites.overview.ausstattung.item.2": { de: "Tresor" },
  "suites.overview.ausstattung.item.3": { de: "Hand- und Badetücher" },
  "suites.overview.ausstattung.item.4": { de: "Föhn" },
  "suites.overview.ausstattung.item.5": { de: "Flat-TV" },
  "suites.overview.ausstattung.item.6": { de: "Backrohr" },
  "suites.overview.ausstattung.item.7": { de: "4-Felder-Herd" },
  "suites.overview.ausstattung.item.8": { de: "Geschirrspüler" },
  "suites.overview.ausstattung.item.9": { de: "Kühlschrank mit Gefrierfach" },
  "suites.overview.ausstattung.item.10": { de: "Kaffeemaschine (Kapseln und Filter)" },
  "suites.overview.ausstattung.item.11": { de: "Wasserkocher" },
  "suites.overview.ausstattung.item.12": { de: "ausreichend Geschirr und Besteck" },
  "suites.overview.ausstattung.item.13": { de: "Spülmittel" },
  "suites.overview.ausstattung.item.14": { de: "Toilettenpapier" },
  "suites.overview.ausstattung.item.15": { de: "Küchenutensilien uvm." },
  "suites.overview.ausstattung.coffee_note": {
    de: "Kaffeepulver ist selbst mitzubringen. Kaffeekapseln können ebenfalls selbst mitgebracht oder an der Rezeption erworben werden.",
    
  },
  "suites.overview.ausstattung.list_left": {
    de: "<ul><li>Bettwäsche</li><li>Kleiderschrank</li><li>Tresor</li><li>Hand- und Badetücher</li><li>Föhn</li><li>Flat-TV</li><li>Backrohr</li><li>4-Felder-Herd</li><li>Geschirrspüler</li><li>Kühlschrank mit Gefrierfach</li></ul>",
    
    html: true,
  },
  "suites.overview.ausstattung.list_right": {
    de: "<ul><li>Kaffeemaschine (Kapseln und Filter)</li><li>Wasserkocher</li><li>ausreichend Geschirr und Besteck</li><li>Spülmittel</li><li>Toilettenpapier</li><li>Küchenutensilien uvm.</li></ul>",
    
    html: true,
  },

  // === Suite Detail ===
  "suite.not_found.title": { de: "Suite nicht gefunden" },
  "suite.not_found.back": { de: "Zurück zur Übersicht" },
  "suite.detail.back": { de: "zurück zur Übersicht" },
  "suite.intro.cta_prices": { de: "Preise & Verfügbarkeit" },
  "suite.intro.lightbox_open_aria": { de: "Bild {n} in Lightbox öffnen" },
  "suite.intro.dot_aria": { de: "Bild {n} anzeigen" },
  "suite.intro.prev_aria": { de: "Vorheriges Bild" },
  "suite.intro.next_aria": { de: "Nächstes Bild" },
  "suite.intro.close_aria": { de: "Schließen" },
  "suite.intro.gallery_aria": { de: "Bildergalerie" },
  "suite.grundriss.enlarge_aria": { de: "Grundriss {title} vergrößern" },
  "suite.grundriss.enlarge_label": { de: "Vergrößern" },
  "suite.grundriss.alt": { de: "Grundriss {title}" },
  "suite.availability.kicker": { de: "Preise & Verfügbarkeit" },
  "suite.availability.headline": { de: "Preiskalender" },
  "suite.availability.loading": { de: "Preise werden geladen …" },
  "suite.availability.error": { de: "Preiskalender konnte nicht geladen werden." },
  "suite.impressionen.alt": { de: "{title} – Impression {n}" },

  // === Footer (erweitert) ===
  "footer.newsletter.kicker": { de: "auf dem Laufenden bleiben" },
  "footer.newsletter.text": {
    de: "Bestens informiert für Ihren nächsten Besuch in den Lerch Suites – mit exklusiven Angeboten, Veranstaltungstipps und persönlichen Empfehlungen.",
    
  },
  "footer.newsletter.button": { de: "Newsletter abonnieren" },
  "footer.region.kappl_aria": { de: "Kappl Tourismus (öffnet in neuem Tab)" },
  "footer.region.ischgl_aria": { de: "Ischgl Tourismus (öffnet in neuem Tab)" },
  "footer.bildmarke_alt": { de: "Lerch Suites" },

  // === Sitemap ===
  "sitemap.seo.title": { de: "Sitemap – Übersicht aller Seiten" },
  "sitemap.seo.description": {
    de: "HTML-Sitemap der Lerch Suites: Übersicht aller Seiten und Suiten auf einen Blick.",
    
  },
  "sitemap.kicker": { de: "Übersicht" },
  "sitemap.headline": { de: "Sitemap" },
  "sitemap.intro": {
    de: "Hier finden Sie eine vollständige Übersicht aller Seiten der Lerch Suites Website.",
    
  },
  "sitemap.section.main": { de: "Hauptseiten" },
  "sitemap.section.suites": { de: "Suiten" },
  "sitemap.section.legal": { de: "Rechtliches" },
  "sitemap.page.home": { de: "Startseite" },
  "sitemap.page.suites": { de: "Suiten" },
  "sitemap.page.lerch_plus": { de: "Lerch Plus" },
  "sitemap.page.wellness": { de: "Wellness" },
  "sitemap.page.erlebnisse": { de: "Erlebnisse" },
  "sitemap.page.sommer": { de: "Erlebnisse – Sommer" },
  "sitemap.page.winter": { de: "Erlebnisse – Winter" },
  "sitemap.page.events": { de: "Top-Events im Paznaun" },
  "sitemap.page.bewertungen": { de: "Bewertungen" },
  "sitemap.page.lage": { de: "Lage & Anreise" },
  "sitemap.page.buchen": { de: "Jetzt buchen" },
  "sitemap.page.anfrage": { de: "Unverbindliche Anfrage" },
  "sitemap.page.impressum": { de: "Impressum" },
  "sitemap.page.datenschutz": { de: "Datenschutz" },
  "sitemap.page.barrierefreiheit": { de: "Barrierefreiheitserklärung" },

  // === Legal: gemeinsame Labels ===
  "legal.kicker": { de: "Rechtliches" },

  // === Impressum ===
  "impressum.seo.title": { de: "Impressum" },
  "impressum.seo.description": {
    de: "Impressum & Offenlegung – Rechtliche Informationen zu Lerch Suites in Kappl, Tirol.",
    
  },
  "impressum.headline": { de: "Impressum & Offenlegung" },
  "impressum.intro": {
    de: "Rechtliche Hinweise und Offenlegungspflichten gemäß § 5 ECG, § 14 UGB und § 25 MedienG.",
    
  },
  "impressum.s1.title": { de: "Betreiber / Inhaber" },
  "impressum.s1.body": {
    de: `<p class="font-semibold text-foreground">Lerch Suites</p><p>Rebecca Lerch</p><p>Wiese 103</p><p>6555 Kappl</p><p class="pt-2">Mail: <a href="mailto:info@lerch-suites.at" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">info@lerch-suites.at</a></p><p>Tel.: <a href="tel:+436645380244" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">0043 664 5380244</a></p><p class="pt-2">UID: ATU73143323</p>`,
    en: `<p class="font-semibold text-foreground">Lerch Suites</p><p>Rebecca Lerch</p><p>Wiese 103</p><p>6555 Kappl</p><p class="pt-2">Mail: <a href="mailto:info@lerch-suites.at" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">info@lerch-suites.at</a></p><p>Phone: <a href="tel:+436645380244" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">0043 664 5380244</a></p><p class="pt-2">VAT ID: ATU73143323</p>`,
    html: true,
  },
  "impressum.s2.title": { de: "Unternehmensdaten" },
  "impressum.s2.body": {
    de: `<p><span class="font-semibold">Rechtsform:</span> Einzelunternehmen</p><p><span class="font-semibold">Ort der Gewerbeberechtigung:</span> Wiese 103, 6555 Kappl</p><p><span class="font-semibold">Gerichtsstand:</span> Kappl</p><p><span class="font-semibold">Kammerzugehörigkeit:</span> WKO Tirol</p><p><span class="font-semibold">Anwendbare gewerbe- oder berufsrechtliche Vorschriften:</span> Gewerbeordnung 1994, abrufbar unter <a href="http://www.ris.bka.gv.at" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">www.ris.bka.gv.at</a></p>`,
    en: `<p><span class="font-semibold">Legal form:</span> Sole proprietorship</p><p><span class="font-semibold">Place of trade licence:</span> Wiese 103, 6555 Kappl</p><p><span class="font-semibold">Place of jurisdiction:</span> Kappl</p><p><span class="font-semibold">Chamber membership:</span> WKO Tyrol</p><p><span class="font-semibold">Applicable trade or professional regulations:</span> Austrian Trade Code 1994 (Gewerbeordnung 1994), available at <a href="http://www.ris.bka.gv.at" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">www.ris.bka.gv.at</a></p>`,
    html: true,
  },
  "impressum.s3.title": { de: "Design, Programmierung & Hosting" },
  "impressum.s3.body": {
    de: `<p class="font-semibold text-foreground">Huber Web Media GmbH</p><p>Mils Au 70</p><p>6493 Mils bei Imst</p><p>Tirol – Österreich</p><p class="pt-2">Tel.: <a href="tel:+43541820877" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">+43 5418 20877</a></p><p>E-Mail: <a href="mailto:welcome@huberwebmedia.at" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">welcome@huberwebmedia.at</a></p><p>Web: <a href="https://www.huberwebmedia.at" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">www.huberwebmedia.at</a></p><p>UID: ATU 77423049</p>`,
    en: `<p class="font-semibold text-foreground">Huber Web Media GmbH</p><p>Mils Au 70</p><p>6493 Mils bei Imst</p><p>Tyrol – Austria</p><p class="pt-2">Phone: <a href="tel:+43541820877" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">+43 5418 20877</a></p><p>E-mail: <a href="mailto:welcome@huberwebmedia.at" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">welcome@huberwebmedia.at</a></p><p>Web: <a href="https://www.huberwebmedia.at" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">www.huberwebmedia.at</a></p><p>VAT ID: ATU 77423049</p>`,
    html: true,
  },
  "impressum.s4.title": { de: "Haftungsausschluss" },
  "impressum.s4.body": {
    de: `<p>Sämtliche Inhalte wurden sorgfältig geprüft. Dessen ungeachtet kann keine Garantie für Richtigkeit, Vollständigkeit oder Aktualität der Angaben übernommen werden. Haftungsansprüche, die durch die Nutzung oder Nichtnutzung der dargebotenen Informationen bzw. durch die Nutzung fehlerhafter und unvollständiger Informationen verursacht wurden, sind grundsätzlich ausgeschlossen.</p>`,
    en: `<p>All content has been carefully checked. Nevertheless, no guarantee can be given for the accuracy, completeness or topicality of the information. Liability claims arising from the use or non-use of the information provided or from the use of incorrect and incomplete information are generally excluded.</p>`,
    html: true,
  },
  "impressum.s5.title": { de: "Haftungsausschluss für Links" },
  "impressum.s5.body": {
    de: `<p>Unsere Webseite enthält Links zu externen Webseiten. Auf den Inhalt dieser externen Webseiten haben wir keinerlei Einfluss, deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar.</p>`,
    en: `<p>Our website contains links to external websites. We have no influence on the content of these external websites, which is why we cannot accept any liability for this external content. The respective provider or operator of the linked pages is always responsible for their content. No unlawful content was recognisable at the time of linking.</p>`,
    html: true,
  },
  "impressum.s6.title": { de: "Bildrechte / Bildquellen" },
  "impressum.s6.body": {
    de: `<p>Das auf dieser Internetseite dargestellte Bildmaterial ist urheberrechtlich geschützt. Eine Nutzung ohne Zustimmung ist nicht gestattet.</p><ul class="mt-4 space-y-2 list-none pl-0"><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Suiten – Impressionen (Austria, Deluxe, Garten, Swiss Suite):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Rossner Photographie</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Suiten – Küchenbilder:</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Arlberg Fotografie</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Suiten – Familie Lerch, Essens-Stimmungen:</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Huber Web Media</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Suiten – Grundrisse:</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Lerch Suites</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Hero, Wellness (Sauna, Ruhebereich, Fußbad, Infrarot):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Lerch Suites</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Silvretta Therme Ischgl (Sauna- & Ruhebereich):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Silvrettaseilbahn AG / Kürzi</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Lerch Plus (Skiraum, Ladestation, Getränke, Kinderspielplatz, Lage, Gästemappe):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Lerch Suites</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Restaurants & Bars (Kebap Corner, Post Kappl, Mountaingarage, Salnerhof, Nevada, Trattoria):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© jeweilige Betriebe</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Erlebnisse Sommer (Wandern, Biken, Silvretta Card, Sunny Mountain, Schlechtwetter):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© TVB Paznaun – Ischgl</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Erlebnisse Winter (Silvretta Arena, Skischaukel, Rodeln, Sunny Mountain):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© TVB Paznaun – Ischgl</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Top Events (Sommer & Winter):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© TVB Paznaun – Ischgl</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Logos Ischgl & Kappl:</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© TVB Paznaun – Ischgl</span></li></ul><p class="pt-4 text-foreground/70 italic">© Lerch Suites – Alle Rechte vorbehalten.</p>`,
    en: `<p>The images shown on this website are protected by copyright. Use without consent is not permitted.</p><ul class="mt-4 space-y-2 list-none pl-0"><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Apartments – impressions (Austria, Deluxe, Garten, Swiss Suite):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Rossner Photographie</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Apartments – kitchen photos:</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Arlberg Fotografie</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Apartments – Lerch family, food impressions:</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Huber Web Media</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Apartments – floor plans:</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Lerch Suites</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Hero, wellness (sauna, relaxation area, foot bath, infrared):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Lerch Suites</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Silvretta Therme Ischgl (sauna & relaxation area):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Silvrettaseilbahn AG / Kürzi</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Lerch Plus (ski room, charging station, drinks, children's playground, location, guest folder):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© Lerch Suites</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Restaurants & bars (Kebap Corner, Post Kappl, Mountaingarage, Salnerhof, Nevada, Trattoria):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© respective establishments</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Summer experiences (hiking, biking, Silvretta Card, Sunny Mountain, bad weather):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© TVB Paznaun – Ischgl</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Winter experiences (Silvretta Arena, ski circuit, tobogganing, Sunny Mountain):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© TVB Paznaun – Ischgl</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Top events (summer & winter):</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© TVB Paznaun – Ischgl</span></li><li class="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-3 border-b border-foreground/10 pb-2"><span class="text-foreground/80">Logos Ischgl & Kappl:</span><span class="font-medium whitespace-nowrap" style="color:#6C184F">© TVB Paznaun – Ischgl</span></li></ul><p class="pt-4 text-foreground/70 italic">© Lerch Suites – all rights reserved.</p>`,
    html: true,
  },
  "impressum.s7.title": { de: "Urheberrechte" },
  "impressum.s7.body": {
    de: `<p>Sämtliche Texte, Grafiken und Bilder sind urheberrechtlich geschützt. Eine Weiterverwendung ist nur mit entsprechender Genehmigung zulässig.</p>`,
    en: `<p>All texts, graphics and images are protected by copyright. Reuse is only permitted with appropriate authorisation.</p>`,
    html: true,
  },
  "impressum.s8.title": { de: "Zweck der Webseite" },
  "impressum.s8.body": {
    de: `<p>Diese Webseite dient der Information über Waren und Dienstleistungen des Unternehmens sowie der Förderung des Absatzes derselben.</p>`,
    en: `<p>This website serves to inform about the company's goods and services and to promote their sale.</p>`,
    html: true,
  },

  // === Datenschutz ===
  "datenschutz.seo.title": { de: "Datenschutz" },
  "datenschutz.seo.description": {
    de: "Datenschutzerklärung der Lerch Suites – Informationen zur Verarbeitung Ihrer Daten gemäß DSGVO.",
    
  },
  "datenschutz.headline": { de: "Datenschutzerklärung" },
  "datenschutz.intro": {
    de: "Informationen zur Verarbeitung Ihrer personenbezogenen Daten gemäß DSGVO und österreichischem Datenschutzgesetz.",
    
  },
  "datenschutz.s1.title": { de: "Allgemeines" },
  "datenschutz.s1.body": {
    de: `<p>Der Schutz Ihrer persönlichen Daten ist uns ein besonderes Anliegen. Wir verarbeiten Ihre Daten daher ausschließlich auf Grundlage der gesetzlichen Bestimmungen (DSGVO, DSG). In diesen Datenschutzinformationen informieren wir Sie über die wichtigsten Aspekte der Datenverarbeitung im Rahmen unserer Webseite.</p><p>Sofern innerhalb dieser Webseite die Möglichkeit zur Eingabe persönlicher oder geschäftlicher Daten besteht, so erfolgt die Preisgabe dieser Daten seitens des Nutzers ausdrücklich freiwillig.</p>`,
    en: `<p>The protection of your personal data is of particular importance to us. We therefore process your data exclusively on the basis of statutory provisions (GDPR, Austrian Data Protection Act). In this privacy information we inform you about the most important aspects of data processing in the context of our website.</p><p>If there is the possibility of entering personal or business data on this website, the disclosure of this data by the user is expressly voluntary.</p>`,
    html: true,
  },
  "datenschutz.s2.title": { de: "Kontaktaufnahme" },
  "datenschutz.s2.body": {
    de: `<p>Wenn Sie per Formular auf der Webseite oder per E-Mail Kontakt mit uns aufnehmen, werden Ihre angegebenen Daten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen sechs Monate bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.</p>`,
    en: `<p>If you contact us via a form on the website or by e-mail, the data you provide will be stored by us for six months for the purpose of processing the enquiry and in case of follow-up questions. We will not pass on this data without your consent.</p>`,
    html: true,
  },
  "datenschutz.s3.title": { de: "Cookies" },
  "datenschutz.s3.body": {
    de: `<p>Cookies sind kleine Dateien, die es dieser Webseite ermöglichen, auf dem Computer des Besuchers spezifische, auf den Nutzer bezogene Informationen zu speichern, während unsere Webseite besucht wird. Cookies helfen uns dabei, die Nutzungshäufigkeit und die Anzahl der Nutzer unserer Internetseiten zu ermitteln sowie unsere Angebote für Sie komfortabel und effizient zu gestalten.</p><p>Wir verwenden einerseits Session-Cookies, die ausschließlich für die Dauer Ihrer Nutzung unserer Webseite zwischengespeichert werden, und zum anderen permanente Cookies, um Informationen über Besucher festzuhalten, die wiederholt auf unsere Webseite zugreifen. Der Zweck des Einsatzes dieser Cookies besteht darin, eine optimale Benutzerführung anbieten zu können sowie Besucher wiederzuerkennen und bei wiederholter Nutzung eine möglichst attraktive Webseite und interessante Inhalte präsentieren zu können. Der Inhalt eines permanenten Cookies beschränkt sich auf eine Identifikationsnummer. Name, IP-Adresse usw. werden nicht gespeichert. Eine Einzelprofilbildung über Ihr Nutzungsverhalten findet nicht statt.</p><p>Eine Nutzung unserer Angebote ist auch ohne Cookies möglich. Sie können in Ihrem Browser das Speichern von Cookies deaktivieren, auf bestimmte Webseiten beschränken oder Ihren Webbrowser so einstellen, dass er Sie benachrichtigt, sobald ein Cookie gesendet wird. Sie können Cookies auch jederzeit von der Festplatte Ihres PCs löschen. Bitte beachten Sie jedoch, dass Sie in diesem Fall mit einer eingeschränkten Darstellung der Seite und einer eingeschränkten Benutzerführung rechnen müssen.</p>`,
    en: `<p>Cookies are small files that enable this website to store specific, user-related information on the visitor's computer while our website is being visited. Cookies help us to determine the frequency of use and number of users of our websites and to make our offers convenient and efficient for you.</p><p>On the one hand, we use session cookies, which are only cached for the duration of your use of our website, and on the other hand, permanent cookies to record information about visitors who repeatedly access our website. The purpose of using these cookies is to be able to offer optimal user guidance, to recognise visitors and to present the most attractive website and interesting content possible during repeated use. The content of a permanent cookie is limited to an identification number. Name, IP address, etc. are not stored. No individual profile is created about your usage behaviour.</p><p>Our offers can also be used without cookies. You can deactivate the storage of cookies in your browser, restrict them to certain websites or set your web browser to notify you as soon as a cookie is sent. You can also delete cookies from the hard drive of your PC at any time. Please note, however, that in this case you must expect a restricted display of the page and restricted user guidance.</p>`,
    html: true,
  },
  "datenschutz.s4.title": { de: "Google Analytics" },
  "datenschutz.s4.body": {
    de: `<p>Diese Webseite benutzt Google Analytics, einen Webanalysedienst der Google Inc. („Google"). Google Analytics verwendet Cookies, Textdateien, die auf Ihrem Computer gespeichert werden und eine Analyse der Benutzung der Webseite durch Sie ermöglichen. Die durch den Cookie erzeugten Informationen über Ihre Benutzung dieser Webseite werden in der Regel an einen Server von Google in den USA übertragen und dort gespeichert. Im Falle der Aktivierung der IP-Anonymisierung auf dieser Webseite wird Ihre IP-Adresse von Google jedoch innerhalb von Mitgliedstaaten der Europäischen Union oder in anderen Vertragsstaaten des Abkommens über den Europäischen Wirtschaftsraum zuvor gekürzt.</p><p>Im Auftrag des Betreibers dieser Webseite wird Google diese Informationen benutzen, um Ihre Nutzung der Webseite auszuwerten, um Reports über die Webseitenaktivitäten zusammenzustellen und um weitere mit der Webseitennutzung und der Internetnutzung verbundene Dienstleistungen gegenüber dem Webseitenbetreiber zu erbringen. Die im Rahmen von Google Analytics von Ihrem Browser übermittelte IP-Adresse wird nicht mit anderen Daten von Google zusammengeführt.</p><p>Nähere Informationen finden Sie unter <a href="https://www.google.com/analytics/terms/de.html" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">google.com/analytics/terms/de.html</a> sowie <a href="https://support.google.com/analytics/answer/6004245?hl=de" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">support.google.com/analytics/answer/6004245</a>.</p>`,
    en: `<p>This website uses Google Analytics, a web analytics service provided by Google Inc. ("Google"). Google Analytics uses cookies, text files that are stored on your computer and enable an analysis of your use of the website. The information generated by the cookie about your use of this website is generally transmitted to a Google server in the USA and stored there. If IP anonymisation is activated on this website, however, your IP address will be shortened beforehand by Google within member states of the European Union or in other states party to the Agreement on the European Economic Area.</p><p>On behalf of the operator of this website, Google will use this information to evaluate your use of the website, to compile reports on website activity and to provide further services related to website use and internet use to the website operator. The IP address transmitted by your browser as part of Google Analytics is not merged with other Google data.</p><p>Further information can be found at <a href="https://www.google.com/analytics/terms/de.html" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">google.com/analytics/terms</a> and <a href="https://support.google.com/analytics/answer/6004245?hl=de" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">support.google.com/analytics/answer/6004245</a>.</p>`,
    html: true,
  },
  "datenschutz.s5.title": { de: "Google Maps" },
  "datenschutz.s5.body": {
    de: `<p>Diese Webseite verwendet Google Maps für die Darstellung von Karteninformationen. Bei der Nutzung von Google Maps werden von Google auch Daten über die Nutzung der Maps-Funktionen durch Besucher der Webseiten erhoben, verarbeitet und genutzt. Nähere Informationen über die Datenverarbeitung durch Google können Sie den Datenschutzhinweisen von Google unter <a href="https://www.google.at/intl/de/policies/privacy/" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">google.at/intl/de/policies/privacy</a> entnehmen.</p>`,
    en: `<p>This website uses Google Maps to display map information. When using Google Maps, Google also collects, processes and uses data on the use of the Maps functions by visitors to the websites. You can find more information about data processing by Google in Google's privacy policy at <a href="https://www.google.at/intl/de/policies/privacy/" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">google.at/intl/en/policies/privacy</a>.</p>`,
    html: true,
  },
  "datenschutz.s6.title": { de: "Facebook & Social Plugins" },
  "datenschutz.s6.body": {
    de: `<p>Auf unseren Seiten können Plugins des sozialen Netzwerks Facebook integriert sein. Die Facebook-Plugins erkennen Sie am Facebook-Logo oder dem „Like-Button". Wenn Sie unsere Seiten besuchen, wird über das Plugin eine direkte Verbindung zwischen Ihrem Browser und dem Facebook-Server hergestellt. Wir als Anbieter der Seiten erhalten keine Kenntnis vom Inhalt der übermittelten Daten sowie deren Nutzung durch Facebook. Weitere Informationen finden Sie in der Datenschutzerklärung von Facebook unter <a href="https://www.facebook.com/policy.php" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">facebook.com/policy.php</a>.</p>`,
    en: `<p>Plugins from the social network Facebook may be integrated on our pages. You can recognise the Facebook plugins by the Facebook logo or the "Like button". When you visit our pages, the plugin establishes a direct connection between your browser and the Facebook server. As provider of the pages, we have no knowledge of the content of the transmitted data or its use by Facebook. For more information, please refer to Facebook's privacy policy at <a href="https://www.facebook.com/policy.php" target="_blank" rel="noopener noreferrer" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">facebook.com/policy.php</a>.</p>`,
    html: true,
  },
  "datenschutz.s7.title": { de: "YouTube" },
  "datenschutz.s7.body": {
    de: `<p>Auf unserer Webseite können Funktionen des Dienstes YouTube implementiert sein. Diese Funktionen werden durch die YouTube, LLC angeboten. Die eingebundenen Videos legen beim Aufrufen der Webseite Cookies auf den Computern der User ab. YouTube legt auch in anderen Cookies nicht-personenbezogene Nutzungsinformationen ab. Möchten Sie dies verhindern, müssen Sie dies im Browser blockieren.</p>`,
    en: `<p>Functions of the YouTube service may be implemented on our website. These functions are provided by YouTube, LLC. The embedded videos place cookies on the users' computers when the website is accessed. YouTube also places non-personal usage information in other cookies. If you wish to prevent this, you must block it in your browser.</p>`,
    html: true,
  },
  "datenschutz.s8.title": { de: "Newsletter" },
  "datenschutz.s8.body": {
    de: `<p>Sie haben die Möglichkeit, über unsere Webseite unseren Newsletter zu abonnieren. Hierfür benötigen wir Ihre E-Mail-Adresse und Ihre Einwilligung, dass Sie mit dem Bezug des Newsletters einverstanden sind. Das Abo des Newsletters können Sie jederzeit stornieren.</p>`,
    en: `<p>You have the option of subscribing to our newsletter via our website. For this we need your e-mail address and your consent that you agree to receive the newsletter. You can cancel your subscription to the newsletter at any time.</p>`,
    html: true,
  },
  "datenschutz.s9.title": { de: "Ihre Rechte" },
  "datenschutz.s9.body": {
    de: `<p>Ihnen stehen grundsätzlich die Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit, Widerruf und Widerspruch zu. Wenn Sie glauben, dass die Verarbeitung Ihrer Daten gegen das Datenschutzrecht verstößt oder Ihre datenschutzrechtlichen Ansprüche sonst in einer Weise verletzt worden sind, können Sie sich bei der Aufsichtsbehörde beschweren. In Österreich ist dies die Datenschutzbehörde. Um Ihre Rechte wahrzunehmen, kontaktieren Sie uns bitte über die unten angegebenen Kontaktdaten.</p>`,
    en: `<p>In principle, you have the rights to information, correction, deletion, restriction, data portability, revocation and objection. If you believe that the processing of your data violates data protection law or that your data protection rights have otherwise been infringed in any way, you can lodge a complaint with the supervisory authority. In Austria this is the Data Protection Authority. To exercise your rights, please contact us via the contact details below.</p>`,
    html: true,
  },
  "datenschutz.s10.title": { de: "Sie erreichen uns unter folgenden Kontaktdaten" },
  "datenschutz.s10.body": {
    de: `<p class="font-semibold text-foreground">Lerch Suites</p><p>Rebecca Lerch</p><p>Wiese 103</p><p>6555 Kappl</p><p class="pt-2">Tel.: <a href="tel:+436645380244" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">0043 664 5380244</a></p><p>E-Mail: <a href="mailto:info@lerch-suites.at" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">info@lerch-suites.at</a></p>`,
    en: `<p class="font-semibold text-foreground">Lerch Suites</p><p>Rebecca Lerch</p><p>Wiese 103</p><p>6555 Kappl</p><p class="pt-2">Phone: <a href="tel:+436645380244" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">0043 664 5380244</a></p><p>E-mail: <a href="mailto:info@lerch-suites.at" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">info@lerch-suites.at</a></p>`,
    html: true,
  },

  // === Barrierefreiheit ===
  "barr.seo.title": { de: "Barrierefreiheit" },
  "barr.seo.description": {
    de: "Barrierefreiheits-Erklärung gemäß Web-Zugänglichkeits-Gesetz (WZG) und Richtlinie (EU) 2016/2102.",
    
  },
  "barr.headline": { de: "Erklärung zur Barrierefreiheit" },
  "barr.intro": {
    de: "Diese Erklärung gilt gemäß Web-Zugänglichkeits-Gesetz (WZG) und Richtlinie (EU) 2016/2102 für die Website lerch-suites.at.",
    
  },
  "barr.s1.title": { de: "Einleitung" },
  "barr.s1.body": {
    de: `<p>Die Huber Web Media GmbH ist bemüht, die Website lerch-suites.at im Einklang mit dem <span class="font-semibold">Web-Zugänglichkeits-Gesetz (WZG)</span> sowie der <span class="font-semibold">Richtlinie (EU) 2016/2102</span> barrierefrei zugänglich zu machen.</p>`,
    en: `<p>Huber Web Media GmbH endeavours to make the website lerch-suites.at accessible in accordance with the <span class="font-semibold">Austrian Web Accessibility Act (WZG)</span> and <span class="font-semibold">Directive (EU) 2016/2102</span>.</p>`,
    html: true,
  },
  "barr.s2.title": { de: "Stand der Vereinbarkeit mit den Anforderungen" },
  "barr.s2.body": {
    de: `<p>Die Website ist <span class="font-semibold">teilweise mit Konformitätsstufe AA</span> der Web Content Accessibility Guidelines (WCAG) 2.1 vereinbar. Die Bewertung der Website erfolgte durch eine Selbstbewertung mithilfe des Prüftools „WAVE – Web Accessibility Evaluation Tools" (Stand: 06/2026).</p>`,
    en: `<p>The website is <span class="font-semibold">partially compliant with level AA</span> of the Web Content Accessibility Guidelines (WCAG) 2.1. The website was assessed by means of a self-assessment using the "WAVE – Web Accessibility Evaluation Tools" test tool (as of 06/2026).</p>`,
    html: true,
  },
  "barr.s3.title": { de: "Nicht barrierefreie Inhalte" },
  "barr.s3.intro": { de: "Die nachstehenden Inhalte sind aus folgenden Gründen nicht barrierefrei:" },
  "barr.s3.outro": { de: "Wir arbeiten kontinuierlich daran, bestehende Barrieren zu identifizieren und zu beheben." },
  "barr.s3.item.1": { de: "Bilder ohne Alternativtexte (z. B. dekorative oder inhaltlich relevante Bilder ohne alt-Attribut)" },
  "barr.s3.item.2": { de: "Unzureichende Farbkontraste (z. B. Text auf farbigem Hintergrund mit zu geringem Kontrast)" },
  "barr.s3.item.3": { de: "Nicht bedienbare Navigation per Tastatur (z. B. Dropdown-Menüs oder interaktive Elemente, die nur mit der Maus bedienbar sind)" },
  "barr.s3.item.4": { de: "Formulare ohne korrekte Beschriftung (z. B. fehlende Label-Elemente oder nicht sprechende Fehlermeldungen)" },
  "barr.s3.item.5": { de: "PDF- und Office-Dokumente, die nicht barrierefrei aufbereitet wurden" },
  "barr.s3.item.6": { de: "Nicht deklarierte Sprachwechsel im Text" },
  "barr.s3.item.7": { de: "Tabellen ohne semantische Struktur" },
  "barr.s3.item.8": { de: "Bedienelemente ohne zugängliche Beschriftung (z. B. Buttons mit Icons ohne ARIA-Label)" },
  "barr.s3.item.9": { de: "Videos ohne Untertitel oder Audiodeskription" },
  "barr.s3.item.10": { de: "Unzureichende Fokusführung" },
  "barr.s3.item.11": { de: "CAPTCHA ohne barrierefreie Alternative" },
  "barr.s3.item.12": { de: "Inhalte, die sich nicht pausieren lassen, wenn sie animiert oder bewegt werden" },
  "barr.s3.item.13": { de: "Nicht semantisch korrekte HTML-Struktur" },
  "barr.s3.item.14": { de: "Fehlende Skip-Links zur Navigation" },
  "barr.s4.title": { de: "Erstellung dieser Erklärung" },
  "barr.s4.body": {
    de: `<p>Die Erklärung wurde am <span class="font-semibold">03.06.2026</span> erstellt.</p><p>Die Einschätzung der Website basiert auf einer <span class="font-semibold">Selbstbewertung</span> gemäß den Anforderungen der Richtlinie (EU) 2016/2102.</p>`,
    en: `<p>This statement was prepared on <span class="font-semibold">03/06/2026</span>.</p><p>The assessment of the website is based on a <span class="font-semibold">self-assessment</span> in accordance with the requirements of Directive (EU) 2016/2102.</p>`,
    html: true,
  },
  "barr.s5.title": { de: "Feedback und Kontaktangaben" },
  "barr.s5.body": {
    de: `<p>Sollten Ihnen Barrieren auf der Website auffallen oder Inhalte schwer zugänglich sein, bitten wir um Ihre Rückmeldung:</p><div class="mt-4 p-6 rounded-2xl border border-foreground/10 bg-foreground/[0.02]"><p class="font-semibold text-foreground">Huber Web Media GmbH</p><p>Mils Au 70</p><p>6493 Mils bei Imst</p><p class="pt-2">E-Mail: <a href="mailto:welcome@huberwebmedia.at" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">welcome@huberwebmedia.at</a></p><p>Telefon: <a href="tel:+43541820877" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">+43 5418 20877</a></p></div>`,
    en: `<p>If you notice any barriers on the website or have difficulty accessing content, we kindly ask for your feedback:</p><div class="mt-4 p-6 rounded-2xl border border-foreground/10 bg-foreground/[0.02]"><p class="font-semibold text-foreground">Huber Web Media GmbH</p><p>Mils Au 70</p><p>6493 Mils bei Imst</p><p class="pt-2">E-mail: <a href="mailto:welcome@huberwebmedia.at" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">welcome@huberwebmedia.at</a></p><p>Phone: <a href="tel:+43541820877" class="underline underline-offset-[3px] hover:opacity-70 transition-opacity" style="color:#6C184F">+43 5418 20877</a></p></div>`,
    html: true,
  },

  // === Externe URLs (eigene URL-Felder im Übersetzungs-Backend) ===
  // Lerch Plus – Restaurants
  "lerch_plus.restaurants.post_url": { de: "https://post-kappl.at/" },
  "lerch_plus.restaurants.mountaingarage_url": { de: "https://www.mountaingarage.org/" },
  "lerch_plus.restaurants.salnerhof_url": { de: "https://www.salnerhof.at/" },
  "lerch_plus.restaurants.kebap_url": { de: "https://www.nevada.at/" },
  "lerch_plus.restaurants.trattoria_url": { de: "https://www.silvrettatherme.at/de/Kulinarik/Trattoria" },
  "lerch_plus.restaurants.nevada_url": { de: "https://www.kebapcorner.at/" },
  // Footer – Region- & Service-Links
  "footer.region.kappl_url": { de: "https://service.kappl.com/de/sommer?affiliate=true" },
  "footer.region.ischgl_url": { de: "https://service.ischgl.com/de/sommer?affiliate=true" },
  "footer.site_by_url": { de: "https://huberwebmedia.at" },

  // === Suite-Detailseiten: pro Suite eigener Namespace ===
  // Deluxe Suite
  "suite_deluxe.title": { de: "Deluxe Suite" },
  "suite_deluxe.subline": { de: "Extravaganz & Design auf\u00A095\u00A0m²" },
  "suite_deluxe.intro_text": {
    de: "Die Deluxe Suite bietet auf <strong>95 m² Wohnfläche</strong><br />sowie 27 m² Balkon oder Terrasse großzügigen Platz für Ihren Urlaub. <strong>Drei Doppelzimmer</strong> mit jeweils eigenem Bad mit Regendusche und WC sorgen für Privatsphäre und Komfort. Der offene Wohnbereich mit voll ausgestatteter Küche, gemütlicher Sitzecke und zusätzlicher Schlafcouch mit hochwertiger Matratze bieten Raum für gemeinsame Zeit und entspannte Stunden. Ideal für <strong>6 bis 8&nbsp;Personen</strong>.",
    
    html: true,
  },
  "suite_deluxe.intro_image_alt": { de: "Deluxe Suite – Küche" },
  "suite_deluxe.highlights_subline": { de: "Viel Platz und hochwertige Ausstattung" },
  "suite_deluxe.highlights_headline": { de: "Highlights der Deluxe Suite" },
  "suite_deluxe.highlights_text": {
    de: "Drei Doppelzimmer, ein offener Wohnbereich mit voll ausgestatteter Küche sowie zwei zusätzliche Schlafmöglichkeiten im Wohnbereich bieten viel Platz für Ihren Aufenthalt. Flat-TVs in allen Räumen und der Zugang zur Terrasse mit schöner Aussicht sorgen für zusätzlichen Komfort.",
    
  },
  "suite_deluxe.features_headline": { de: "Ausstattung und Komfort" },
  "suite_deluxe.features_text": {
    de: "3 Doppelzimmer\n1 Schlafcouch für 2 Personen im Wohnraum\nBad mit Regendusche und WC zu jedem Zimmer, Handtücher, Föhn\nOffener Wohnraum mit voll ausgestatteter Küche: Geschirrspüler, Herd, Backofen, Mikrowelle, Kaffeemaschine, Wasserkocher\nUnterhaltung: Flat-TVs in allen Schlafzimmern, WLAN\nTerrasse mit Ausblick",
    
  },

  // Swiss Suite
  "suite_swiss.title": { de: "Swiss Suite" },
  "suite_swiss.subline": { de: "Unvergleichliches Wohlfühlerlebnis auf\u00A070\u00A0m²" },
  "suite_swiss.intro_text": {
    de: "Die Swiss Suite bietet auf <strong>70 m² Wohnfläche</strong><br />sowie einem 30 m² großen Balkon viel Platz für einen entspannten Aufenthalt in Kappl. Ein Doppelzimmer, ein Dreibettzimmer und ein <strong>offener Wohnbereich mit voll ausgestatteter Küche</strong> schaffen eine angenehme, offene Atmosphäre. Eine zusätzliche Schlafmöglichkeit im Wohnbereich ergänzt das Raumangebot. Jedes Zimmer verfügt über ein eigenes Bad mit Regendusche und WC. Ideal für <strong>4 bis 7&nbsp;Personen</strong>.",
    
    html: true,
  },
  "suite_swiss.intro_image_alt": { de: "Swiss Suite – Küche" },
  "suite_swiss.highlights_subline": { de: "Durchdacht ausgestattet mit viel Raum" },
  "suite_swiss.highlights_headline": { de: "Highlights der Swiss Suite" },
  "suite_swiss.highlights_text": {
    de: "Ein Doppelzimmer und ein Dreibettzimmer sowie ein offener Wohnbereich mit voll ausgestatteter Küche bieten angenehmen Wohnkomfort. Zwei zusätzliche Schlafmöglichkeiten im Wohnbereich, Flat-TVs in allen Räumen und der Zugang zum Balkon mit schöner Aussicht aus allen Zimmern runden das Angebot ab.",
    
  },
  "suite_swiss.features_headline": { de: "Ausstattung und Komfort" },
  "suite_swiss.features_text": {
    de: "1 Doppelzimmer\n1 Dreibettzimmer\n1 Schlafcouch für 2 Personen im Wohnraum\nBad mit Regendusche und WC zu jedem Zimmer, Handtücher, Föhn\nOffener Wohnraum mit voll ausgestatteter Küche: Geschirrspüler, Herd, Backofen, Mikrowelle, Kaffeemaschine, Wasserkocher\nUnterhaltung: Flat-TVs in allen Schlafzimmern, WLAN\nVon allen Räumen Zugang zum Balkon mit imposantem Ausblick",
    
  },

  // Garten Suite
  "suite_garten.title": { de: "Garten Suite" },
  "suite_garten.subline": { de: "Ihr privates Gartenparadies auf\u00A070\u00A0m²" },
  "suite_garten.intro_text": {
    de: "Die Garten Suite bietet auf <strong>70 m² Wohnfläche</strong><br />sowie einer 30 m² großen <strong>Terrasse mit eigenem Garten</strong> ein besonderes Urlaubsgefühl für Ihren Aufenthalt bei uns in Kappl. Helle Räume, eine hochwertige Ausstattung und durchdachte Details schaffen eine angenehme Atmosphäre. Ein Doppelzimmer, ein Dreibettzimmer und eine zusätzliche Schlafcouch mit hochwertiger Matratze im Wohnbereich bieten Platz für <strong>4 bis 7 Personen</strong>. Alle Schlafzimmer verfügen über ein eigenes Bad mit Regendusche&nbsp;und&nbsp;WC.",
    
    html: true,
  },
  "suite_garten.intro_image_alt": { de: "Garten Suite – Küche" },
  "suite_garten.highlights_subline": { de: "Viel Raum mit eigener Terrasse und Garten" },
  "suite_garten.highlights_headline": { de: "Highlights der Garten Suite" },
  "suite_garten.highlights_text": {
    de: "Ein Doppelzimmer und ein Dreibettzimmer sowie ein offener Wohnbereich mit voll ausgestatteter Küche bieten angenehmen Wohnkomfort. Zwei zusätzliche Schlafmöglichkeiten im Wohnbereich schaffen weiteren Platz und der eigene Garten mit Terrasse lädt zum Entspannen ein.",
    
  },
  "suite_garten.features_headline": { de: "Ausstattung und Komfort" },
  "suite_garten.features_text": {
    de: "1 Doppelzimmer\n1 Dreibettzimmer\n1 Schlafcouch für 2 Personen im Wohnraum\nBad mit Regendusche und WC zu jedem Zimmer, Handtücher, Föhn\nOffener Wohnraum mit voll ausgestatteter Küche: Geschirrspüler, Herd, Backofen, Mikrowelle, Kaffeemaschine, Wasserkocher\nUnterhaltung: Flat-TVs in allen Schlafzimmern, WLAN\nEigener Garten mit wunderschöner Terrasse, Zugang von allen Zimmern",
    
  },

  // Austria Suite
  "suite_austria.title": { de: "Austria Suite" },
  "suite_austria.subline": { de: "Freiraum & Entspannung auf\u00A090\u00A0m²" },
  "suite_austria.intro_text": {
    de: "Die Austria Suite bietet auf <strong>90 m² Wohnfläche</strong><br />sowie einem 30 m² großen Balkon viel Raum für Ihren Aufenthalt. <strong>Zwei Doppelzimmer und ein Dreibettzimmer</strong> sorgen für eine angenehme Aufteilung, ergänzt durch einen offenen Wohnbereich mit voll ausgestatteter Küche. Eine zusätzliche Schlafcouch mit hochwertiger Matratze im Wohnbereich erweitert das Platzangebot. Alle Zimmer verfügen über ein eigenes Bad mit Regendusche und WC. Ideal für <strong>6 bis 9&nbsp;Personen</strong>.",
    
    html: true,
  },
  "suite_austria.intro_image_alt": { de: "Austria Suite – Küche" },
  "suite_austria.highlights_subline": { de: "Ideale Raumaufteilung und moderne Einrichtung" },
  "suite_austria.highlights_headline": { de: "Highlights der Austria Suite" },
  "suite_austria.highlights_text": {
    de: "Zwei Doppelzimmer und ein Dreibettzimmer sowie ein offener Wohnbereich mit voll ausgestatteter Küche bieten viel Raum für Ihren Aufenthalt in diesem Apartment. Zwei zusätzliche Schlafmöglichkeiten im Wohnbereich schaffen weiteren Platz. Der Zugang zum Balkon mit schöner Aussicht aus allen Zimmern sorgt für ein rundum angenehmes Wohngefühl.",
    
  },
  "suite_austria.features_headline": { de: "Ausstattung und Komfort" },
  "suite_austria.features_text": {
    de: "2 Doppelzimmer\n1 Dreibettzimmer\n1 Schlafcouch für 2 Personen im Wohnraum\nBad mit Regendusche und WC zu jedem Zimmer, Handtücher, Föhn\nOffener Wohnraum mit voll ausgestatteter Küche: Geschirrspüler, Herd, Backofen, Mikrowelle, Kaffeemaschine, Wasserkocher\nUnterhaltung: Flat-TVs in allen Schlafzimmern, WLAN\nVon allen Räumen Zugang zum Balkon mit beeindruckendem Ausblick",
    
  },
} as const satisfies Record<string, TranslationEntry>;

export type TranslationKey = keyof typeof translations;

/** Stabiler Hash (DJB2) für Fingerprint-Vergleich. */
export function fingerprint(text: string): string {
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash) ^ text.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

/** Englische Default-Übersetzungen nachladen (separater Chunk). */
export const loadEnDefaults = (): Promise<Record<string, string>> =>
  import("./translations.en").then((m) => m.translationsEn);

export function getAllTranslationEntries(enDefaults?: Record<string, string>) {
  return (Object.keys(translations) as TranslationKey[])
    .filter((key) => !key.includes("_aria"))
    .map((key) => {
      const entry = translations[key] as TranslationEntry;
      return {
        key,
        de: entry.de,
        en_default: enDefaults?.[key] ?? entry.en,
        is_html: !!entry.html,
        fingerprint_de: fingerprint(entry.de),
      };
    });
}
